import { API_BASE_URL } from '../config/api';
import { formatAuthHeader, getStoredToken } from '../utils/authUtils';
import { authedFetch } from '../utils/authedFetch';
import { TakeFields, announceTendRun, pickTakeFields } from '../utils/takeState';

/** JSON headers + bearer token when present — analytics event attribution
 * reads the JWT server-side, so every mutation should carry it. */
const authJsonHeaders = (): Record<string, string> => {
  const token = getStoredToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: formatAuthHeader(token) } : {}),
  };
};

/** Bearer-only headers for FormData/no-body requests. */
const authOnlyHeaders = (): Record<string, string> => {
  const token = getStoredToken();
  return token ? { Authorization: formatAuthHeader(token) } : {};
};

export interface SeedRequest {
  content_id: number;
  platforms: string[];
  options?: {
    user_id?: string;
    organization_id?: string;
  };
}

export interface TransformationResponse {
  id: number;
  source_content_id: number;
  source_content_type: string;
  source_title: string;
  source_url?: string;
  transformation_type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'partial';
  original_trust_score?: number;
  original_bias_rating?: string;
  created_at: string;
  completed_at?: string;
  processing_time?: number;
  error_message?: string;
}

export interface SocialContent {
  id: number;
  transformation_id: number;
  platform: string;
  content_format: string;
  primary_text: string;
  secondary_text?: string;
  headline?: string;
  hashtags?: string[];
  mentions?: string[];
  character_count: number;
  word_count: number;
  includes_attribution: boolean;
  attribution_text?: string;
  suggested_media_type?: string;
  media_generation_prompt?: string;
  optimal_posting_time?: string;
  engagement_hooks?: string[];
  media_urls?: string[];
  primary_image_url?: string;
  platform_metadata?: Record<string, unknown>;
  created_at: string;
}

export interface PollinateSegment {
  image_url: string;
  caption: string;
  voiceover?: string;
  duration_seconds: number;
  start_seconds: number;
}

export interface PollinateCaption {
  start: number;
  end: number;
  text: string;
}

export interface PollinateStoryboard {
  voiceover_script?: string;
  segments?: PollinateSegment[];
  captions?: PollinateCaption[];
  call_to_action?: string;
  notes?: string;
}

export interface PollinateVideo {
  id: number;
  transformation_id: number;
  creatomate_render_id?: string;
  template_id: string;
  status: string;
  video_url?: string;
  preview_image_url?: string;
  duration_seconds?: number;
  frame_rate?: number;
  width?: number;
  height?: number;
  file_size_bytes?: number;
  caption_url?: string;
  voiceover_enabled: boolean;
  music_mood?: string;
  selected_images?: string[];
  storyboard?: PollinateStoryboard | null;
  error_message?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface PollinateRequest {
  content_id: number;
  selected_images: string[];
  enable_tts?: boolean;
  music_mood?: string;
  target_platforms?: string[];
  template_id?: string;
  user_id?: string;
  organization_id?: string;
}

export interface PollinateGenerateResponse {
  transformation: TransformationResponse;
  video: PollinateVideo;
}

export interface TransformationResults {
  transformation: {
    id: number;
    status: string;
    source_title?: string;
    source_url?: string;
    processing_time?: number;
    completed_at?: string;
    platforms_requested: string[];
  };
  generated_content: Array<{
    id?: number;  // Social content ID for BLOOM publishing
    platform: string;
    format: string;
    text: string;
    secondary_text?: string;
    headline?: string;
    hashtags: string[];
    mentions: string[];
    character_count: number;
    word_count: number;
    media_type?: string;
    media_prompt?: string;
    optimal_posting_time?: string;
    engagement_hooks: string[];
    attribution_text?: string;
    media_urls: string[];
    primary_image_url?: string;
    platform_metadata: Record<string, unknown>;
    thread_position?: number | null;
    thread_total?: number | null;
    // Video fields (for POLLINATE content)
    video_url?: string | null;
    video_thumbnail_url?: string | null;
    video_duration_seconds?: number | null;
    pollinate_video_id?: number | null;
  }>;
}

class TendApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${API_BASE_URL}/tend`;
  }

  async generateSeed(contentId: number, platforms: string[]): Promise<TransformationResponse> {
    try {
      // Create AbortController for custom timeout (90 seconds for multiple platforms)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000);

      const response = await authedFetch(`${this.baseUrl}/seed/generate`, {
        method: 'POST',
        headers: authJsonHeaders(),
        body: JSON.stringify({
          content_id: contentId,
          platforms: platforms,
          options: {
            user_id: 'web-user', // TODO: Get from auth context
          }
        } as SeedRequest),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to generate SEED content: ${response.statusText}`);
      }

      return await response.json();
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        throw new Error('Request timed out. Try selecting fewer platforms or try again later.');
      }
      console.error('Error generating SEED content:', error);
      throw error;
    }
  }

  async getTransformation(transformationId: number): Promise<TransformationResponse> {
    try {
      const response = await authedFetch(`${this.baseUrl}/seed/transformations/${transformationId}`, {
        method: 'GET',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get transformation: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting transformation:', error);
      throw error;
    }
  }

  async getSocialContent(transformationId: number, platform?: string): Promise<SocialContent[]> {
    try {
      const results = await this.getTransformationResults(transformationId);
      const items = platform
        ? results.generated_content.filter(item => item.platform === platform)
        : results.generated_content;

      return items.map((item) => ({
        id: item.id || 0,  // Use actual social_content ID from backend
        transformation_id: results.transformation.id,
        platform: item.platform,
        content_format: item.format,
        primary_text: item.text,
        secondary_text: item.secondary_text,
        headline: item.headline,
        hashtags: item.hashtags,
        mentions: item.mentions,
        character_count: item.character_count,
        word_count: item.word_count,
        includes_attribution: Boolean(item.attribution_text),
        attribution_text: item.attribution_text,
        suggested_media_type: item.media_type,
        media_generation_prompt: item.media_prompt,
        optimal_posting_time: item.optimal_posting_time,
        engagement_hooks: item.engagement_hooks,
        media_urls: item.media_urls,
        primary_image_url: item.primary_image_url,
        platform_metadata: item.platform_metadata,
        created_at: results.transformation.completed_at || new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error getting social content:', error);
      throw error;
    }
  }

  async getTransformationResults(transformationId: number): Promise<TransformationResults> {
    const response = await authedFetch(`${this.baseUrl}/seed/transformations/${transformationId}/results`, {
      method: 'GET',
      headers: authJsonHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch transformation results: ${response.statusText}`);
    }

    return await response.json();
  }

  async generatePollinate(request: PollinateRequest): Promise<PollinateGenerateResponse> {
    try {
      const response = await authedFetch(`${this.baseUrl}/pollinate/generate`, {
        method: 'POST',
        headers: authJsonHeaders(),
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to generate POLLINATE video: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error generating POLLINATE video:', error);
      throw error;
    }
  }

  async listPollinateVideos(contentId: number): Promise<PollinateVideo[]> {
    try {
      const response = await authedFetch(`${this.baseUrl}/pollinate/content/${contentId}/videos`, {
        method: 'GET',
        headers: authJsonHeaders(),
      });

      if (response.status === 404) {
        return [];
      }

      if (!response.ok) {
        throw new Error(`Failed to load POLLINATE videos: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading POLLINATE videos:', error);
      throw error;
    }
  }

  async getPollinateVideo(videoId: number): Promise<PollinateVideo> {
    try {
      const response = await authedFetch(`${this.baseUrl}/pollinate/videos/${videoId}`, {
        method: 'GET',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch POLLINATE video: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching POLLINATE video:', error);
      throw error;
    }
  }

  async getPollinateTransformation(transformationId: number): Promise<PollinateGenerateResponse> {
    try {
      const response = await authedFetch(`${this.baseUrl}/pollinate/transformations/${transformationId}`, {
        method: 'GET',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch POLLINATE transformation: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching POLLINATE transformation:', error);
      throw error;
    }
  }

  async getContentTendv2Transformations(contentId: number): Promise<TransformationResponse[]> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/content/${contentId}/transformations`, {
        method: 'GET',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        // Return empty array instead of throwing - transformations are optional
        console.warn(`Failed to get TendV2 transformations for ${contentId}: ${response.statusText}`);
        return [];
      }

      return await response.json();
    } catch (error) {
      // Return empty array instead of throwing - transformations are optional
      console.warn('Error getting TendV2 transformations:', error);
      return [];
    }
  }

  async getContentTransformations(contentId: number): Promise<TransformationResponse[]> {
    try {
      // Only fetch SEED transformations - TendV2 has its own separate loading via getTendV2ContentItems
      const response = await authedFetch(`${this.baseUrl}/seed/content/${contentId}/transformations`, {
        method: 'GET',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        console.warn(`Failed to get SEED transformations for ${contentId}: ${response.statusText}`);
        return [];
      }

      return await response.json();
    } catch (error) {
      // Return empty array instead of throwing - transformations are optional
      console.warn('Error getting content transformations:', error);
      return [];
    }
  }

  async analyzeContent(contentId: number): Promise<any> {
    try {
      const response = await authedFetch(`${this.baseUrl}/seed/analyze/${contentId}`, {
        method: 'POST',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to analyze content: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error analyzing content:', error);
      throw error;
    }
  }

  async publishToBluesky(socialContentId: number): Promise<BloomPublishResponse> {
    try {
      const response = await authedFetch(`${this.baseUrl}/bloom/publish`, {
        method: 'POST',
        headers: authJsonHeaders(),
        body: JSON.stringify({
          brand_id: 1, // TODO: Get from user context
          social_content_id: socialContentId,
          platforms: ['bluesky'],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to publish to Bluesky: ${response.statusText}`);
      }

      const jobResult = await response.json();

      // Poll for job completion
      if (jobResult.job_id) {
        return await this.pollJobStatus(jobResult.job_id);
      }

      return jobResult;
    } catch (error) {
      console.error('Error publishing to Bluesky:', error);
      throw error;
    }
  }

  async publishArticleToBluesky(contentId: number): Promise<BloomPublishResponse> {
    try {
      const response = await authedFetch(`${this.baseUrl}/bloom/publish-article`, {
        method: 'POST',
        headers: authJsonHeaders(),
        body: JSON.stringify({
          brand_id: 1, // TODO: Get from user context
          content_id: contentId,
          platform: 'bluesky',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to publish article to Bluesky: ${response.statusText}`);
      }

      const jobResult = await response.json();

      // Poll for job completion
      if (jobResult.job_id) {
        return await this.pollJobStatus(jobResult.job_id);
      }

      return jobResult;
    } catch (error) {
      console.error('Error publishing article to Bluesky:', error);
      throw error;
    }
  }

  private async pollJobStatus(jobId: number, maxAttempts: number = 20): Promise<BloomPublishResponse> {
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second

      const response = await authedFetch(`${this.baseUrl}/bloom/jobs/${jobId}`, {
        method: 'GET',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to get job status');
      }

      const job = await response.json();

      if (job.status === 'published' && job.results && job.results.length > 0) {
        const blueskyResult = job.results.find((r: any) => r.platform === 'bluesky');
        return {
          success: blueskyResult?.status === 'success',
          post_url: blueskyResult?.post_url,
          message: 'Published successfully',
          error: blueskyResult?.error_message
        };
      } else if (job.status === 'failed') {
        return {
          success: false,
          error: job.results?.[0]?.error_message || 'Publishing failed'
        };
      }
    }

    throw new Error('Publishing timed out');
  }

  // Tendv2 Methods
  async getTacticOptions(contentId: number, brandSlug?: string): Promise<Tendv2TacticOption[]> {
    try {
      const params = new URLSearchParams();
      if (contentId) params.append('content_id', String(contentId));
      if (brandSlug) params.append('brand_slug', brandSlug);
      const url = `${this.baseUrl}/tendv2/config/tactics?${params.toString()}`;
      console.log('Fetching tactic options from:', url);

      const response = await authedFetch(url, {
        method: 'GET',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to get tactic options: ${response.status} ${response.statusText}`, errorText);
        throw new Error(`Failed to get tactic options: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Tactic options response:', data);

      // Flatten the tactics from all platforms into a single array
      const allTactics: Tendv2TacticOption[] = [];
      if (data.tactics) {
        for (const platform in data.tactics) {
          if (Array.isArray(data.tactics[platform])) {
            allTactics.push(...data.tactics[platform]);
          }
        }
      }

      console.log(`Processed ${allTactics.length} tactics from ${Object.keys(data.tactics || {}).length} platforms`);
      return allTactics;
    } catch (error) {
      console.error('Error getting tactic options:', error);
      throw error;
    }
  }

  async createTendv2Transform(request: Tendv2TransformRequest): Promise<Tendv2TransformResponse> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout

      // Debug logging to understand the 422 error
      console.log('Tendv2 request being sent:', JSON.stringify(request, null, 2));
      console.log('Request URL:', `${this.baseUrl}/tendv2/transform`);

      // Send the JWT so analytics events attribute the tend to the real user
      const authToken = getStoredToken();
      const response = await authedFetch(`${this.baseUrl}/tendv2/transform`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: formatAuthHeader(authToken) } : {}),
        },
        body: JSON.stringify(request),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Tendv2 error response:', {
          status: response.status,
          statusText: response.statusText,
          errorData: errorData
        });
        throw new Error(errorData.detail || `Failed to create Tendv2 transformation: ${response.statusText}`);
      }

      return await response.json();
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        throw new Error('Request timed out. The transformation is taking longer than expected.');
      }
      console.error('Error creating Tendv2 transformation:', error);
      throw error;
    }
  }

  async getTendv2Transformation(transformationId: number): Promise<Tendv2TransformResponse> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/transform/${transformationId}`, {
        method: 'GET',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to get transformation ${transformationId}:`, errorText);
        throw new Error(`Failed to get Tendv2 transformation: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // The backend now returns a different structure, let's ensure tactic_executions exists
      if (!data.tactic_executions) {
        // Initialize with empty array if not present
        data.tactic_executions = [];
      }

      return data;
    } catch (error) {
      console.error('Error getting Tendv2 transformation:', error);
      throw error;
    }
  }

  // maxAttempts default covers podcasts/videos whose transcript is
  // generated just-in-time before the transformation (up to ~15 min server
  // side) — polling exits as soon as the transform reaches a terminal
  // state, so fast article transforms are unaffected.
  async pollTendv2Transformation(transformationId: number, maxAttempts: number = 540): Promise<Tendv2TransformResponse> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const transformation = await this.getTendv2Transformation(transformationId);

        if (transformation.status === 'completed' || transformation.status === 'failed' || transformation.status === 'partial') {
          return transformation;
        }

        // Reset error if successful
        lastError = null;
      } catch (error: any) {
        console.warn(`Polling attempt ${attempt + 1} failed:`, error);
        lastError = error;

        // If it's a 500 error, wait longer before retrying
        if (error.message?.includes('500') || error.message?.includes('Internal Server Error')) {
          console.log('Server error detected, waiting 5 seconds before retry...');
          await new Promise(resolve => setTimeout(resolve, 5000));
          continue;
        }

        // If it's a 404, the transformation might not exist
        if (error.message?.includes('404')) {
          throw new Error(`Transformation ${transformationId} not found`);
        }

        // For other errors, if we've tried a few times, throw
        if (attempt > 3) {
          throw error;
        }
      }

      // Wait 2 seconds between polls
      await new Promise(resolve => setTimeout(resolve, 2000));
    }

    // If we exhausted attempts and have a last error, throw it
    if (lastError) {
      throw lastError;
    }

    throw new Error('Still working — this item may be transcribing first. The transformation continues in the background; check the tended count in a few minutes.');
  }

  async executeBranch(contentId: number, brandId?: number): Promise<Tendv2TransformResponse> {
    const request: Tendv2TransformRequest = {
      content_id: contentId,
      brand_id: brandId || undefined, // Send brand_id if available
      execution_mode: 'branch',
      parallel_execution: true,
      use_cache: true
    };

    const transformation = await this.createTendv2Transform(request);

    // If processing or pending, poll for completion
    if (transformation.status === 'processing' || transformation.status === 'pending') {
      return this.finishRun(contentId, await this.pollTendv2Transformation(transformation.id));
    }

    return this.finishRun(contentId, transformation);
  }

  /** A run's terminal state: tell the page (the Take strip refetches). */
  private finishRun(contentId: number, transformation: Tendv2TransformResponse): Tendv2TransformResponse {
    announceTendRun(contentId, transformation?.id ?? null);
    return transformation;
  }

  async executePrune(
    contentId: number,
    selectedTacticIds: string[],
    brandId?: number,
    brandSlug?: string,
    /** Take phase 1: takeByTactic = the picker's per-row toggles (tactic id →
        take this run); take = a whole-run switch (kept for API callers). */
    opts: { take?: boolean; takeByTactic?: Record<string, boolean> } = {}
  ): Promise<Tendv2TransformResponse> {
    if (!selectedTacticIds || selectedTacticIds.length === 0) {
      throw new Error('At least one tactic must be selected for prune mode');
    }

    console.log('ExecutePrune called with:', {
      contentId,
      brandId,
      selectedTacticIds
    });

    // We need to get the tactic options to map IDs to platform and tactic_id
    const availableTactics = await this.getTacticOptions(contentId, brandSlug);

    // Convert tactic IDs to TacticSelection format. Keys are "platform:id"
    // from the picker (a tactic id can repeat across channels — the bare
    // id used to resolve to whichever channel listed it first); a bare id
    // is still accepted for older callers.
    const tacticSelections: TacticSelection[] = selectedTacticIds.map(tacticId => {
      const colon = tacticId.indexOf(':');
      const keyPlatform = colon > 0 ? tacticId.slice(0, colon) : null;
      const keyId = colon > 0 ? tacticId.slice(colon + 1) : tacticId;
      // Find the tactic in available tactics to get its platform
      const tactic = keyPlatform
        ? availableTactics.find(t => t.id === keyId && t.platform === keyPlatform)
        : availableTactics.find(t => t.id === tacticId);

      if (!tactic) {
        console.warn(`Tactic not found: ${tacticId}, using fallback parsing`);
        // Fallback: try to parse the ID if it contains a colon
        const [platform, ...tacticParts] = tacticId.split(':');
        const tacticName = tacticParts.join(':');
        return {
          platform: tacticName ? platform : 'unknown',
          tactic_id: tacticName || tacticId
        };
      }

      console.log(`Processing tactic: ${tacticId} -> platform: ${tactic.platform}, tactic_id: ${tactic.id}`);

      const takeForRow = opts.takeByTactic?.[tacticId];
      return {
        platform: tactic.platform,
        tactic_id: tactic.id,
        ...(takeForRow === undefined ? {} : { take: takeForRow }),
      };
    });

    const request: Tendv2TransformRequest = {
      content_id: contentId,
      brand_id: brandId || undefined, // Send brand_id if available
      execution_mode: 'prune',
      selected_tactics: tacticSelections,
      parallel_execution: true,
      use_cache: true,
      ...(opts.take ? { take: true } : {}),
    };

    const transformation = await this.createTendv2Transform(request);

    // If processing or pending, poll for completion
    if (transformation.status === 'processing' || transformation.status === 'pending') {
      return this.finishRun(contentId, await this.pollTendv2Transformation(transformation.id));
    }

    return this.finishRun(contentId, transformation);
  }

  /**
   * Fetch with retry logic for handling transient failures
   * Implements exponential backoff with max 3 retries
   */
  private async fetchWithRetry<T>(
    endpoint: string,
    options: RequestInit = {},
    maxRetries: number = 3
  ): Promise<T | null> {
    const delays = [1000, 2000, 4000]; // Exponential backoff

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await authedFetch(`${this.baseUrl}${endpoint}`, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            ...options.headers,
          },
        });

        if (response.ok) {
          return await response.json();
        }

        // Don't retry on 4xx errors (client errors)
        if (response.status >= 400 && response.status < 500) {
          console.error(`Client error ${response.status} for ${endpoint}`);
          return null;
        }

        // Retry on 5xx errors (server errors)
        if (response.status >= 500 && attempt < maxRetries) {
          const delay = delays[attempt] || 4000;
          console.warn(`Server error ${response.status} for ${endpoint}, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }

        return null;
      } catch (error) {
        // Network errors - retry
        if (attempt < maxRetries) {
          const delay = delays[attempt] || 4000;
          console.warn(`Network error for ${endpoint}, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries}):`, error);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }

        console.error(`Failed after ${maxRetries} retries for ${endpoint}:`, error);
        return null;
      }
    }

    return null;
  }

  /**
   * Get TendV2 content items for a specific content ID
   * Uses retry logic to handle transient server errors
   */
  async getTendV2ContentItems(contentId: number, platform?: string): Promise<Tendv2ContentItem[]> {
    try {
      let endpoint = `/tendv2/content/${contentId}/items`;
      if (platform) {
        endpoint += `?platform=${encodeURIComponent(platform)}`;
      }

      const response = await this.fetchWithRetry<Tendv2ContentResponse>(endpoint);

      if (!response) {
        console.warn(`Failed to fetch TendV2 content for content ID ${contentId}`);
        return [];
      }

      if (!response.success) {
        console.warn(`TendV2 content fetch unsuccessful for content ID ${contentId}:`, response.error);
        return [];
      }

      // Return items directly - backend already provides them as a flat array
      if (response.items && Array.isArray(response.items)) {
        return response.items;
      }

      // Fallback to empty array if no items
      return [];
    } catch (error) {
      console.error('Error fetching TendV2 content items:', error);
      return [];
    }
  }

  /** Add a validated subreddit to the brand's Reddit destination list
   * (server checks existence via Ayrshare + caches flair options).
   * Throws with the server's reason on rejection (doesn't exist,
   * inaccessible, duplicate). */
  async addRedditDestination(brandId: number, name: string): Promise<{
    success: boolean; destination: RedditDestination; reddit_destinations: RedditDestination[];
  }> {
    const response = await authedFetch(`${this.baseUrl}/tendv2/brands/${brandId}/reddit-destinations`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify({ name }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.detail || 'Could not add subreddit');
    return data;
  }

  async removeRedditDestination(brandId: number, name: string): Promise<{
    success: boolean; reddit_destinations: RedditDestination[];
  }> {
    const response = await authedFetch(
      `${this.baseUrl}/tendv2/brands/${brandId}/reddit-destinations/${encodeURIComponent(name)}`,
      { method: 'DELETE', headers: authJsonHeaders() },
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.detail || 'Could not remove subreddit');
    return data;
  }

  /** One hydrated content item by its own id (NOT the source content id).
   * First consumer: the analytics Failures view opening the item's card. */
  async getTendV2ContentItem(itemId: number): Promise<{ success: boolean; item: Tendv2ContentItem }> {
    const response = await authedFetch(`${this.baseUrl}/tendv2/content/${itemId}`);
    if (!response.ok) throw new Error(`Failed to load content item ${itemId}`);
    return response.json();
  }

  /** The exact prompt that generated this content (CS/superadmin only —
   * the gateway hard-gates this path). available:false = no LLM ran
   * (vizard clips, legacy rows). */
  async getContentInputPrompt(itemId: number): Promise<{
    success: boolean;
    available: boolean;
    prompt?: string;
    tactic_id?: string;
    tactic_name?: string | null;
    platform?: string;
    prompt_tokens?: number | null;
    completion_tokens?: number | null;
    execution_time_ms?: number | null;
    created_at?: string | null;
  } | null> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/content/${itemId}/input-prompt`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  }

  /** This item's failed publish legs — the card's details strip shows the
   * platform's error next to the content it rejected. */
  async getContentPublishFailures(itemId: number): Promise<{
    success: boolean;
    failures: Array<{
      /** null for a system hold (nothing was sent, so no history row) */
      history_id: number | null; platforms: string[]; error_message: string | null; created_at: string | null;
      state?: 'retrying' | 'paused' | 'recovered' | 'failed';
      attempts?: number | null; max_attempts?: number | null;
      reason?: string | null; kind?: string | null; recovered_at?: string | null;
      recovered_via?: 'manual' | 'queue' | string | null;
      code?: number | string | null; details?: string | null;
    }>;
  }> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/content/${itemId}/publish-failures`);
      if (!response.ok) return { success: false, failures: [] };
      return response.json();
    } catch {
      return { success: false, failures: [] };
    }
  }

  /**
   * Update a TendV2 content item (for inline editing)
   * @param itemId - The content item ID
   * @param updates - Object with fields to update (primary_text, secondary_text, headline, hashtags, source_title, toggles)
   * @returns Success status with updated_at timestamp
   */
  async updateTendV2Content(
    itemId: number,
    updates: Partial<Pick<Tendv2ContentItem,
      'primary_text' | 'secondary_text' | 'headline' | 'hashtags' |
      'source_title' | 'source_subhead' | 'source_category' | 'source_url' | 'primary_image_url' | 'media_urls' | 'media_order_custom' |
      'tactic_link_enabled' | 'tactic_hashtags_enabled' | 'title_font_size' | 'overlay_template_id' | 'overlay_config' |
      'generated_title' | 'generated_subheader' | 'generated_body' | 'post_title' | 'script' |
      'caption_mode_enabled' | 'overlay_logo_url' | 'overlay_logo_key' | 'overlay_logo_display_mode' |
      'image_fit_mode' | 'image_fit_anchor' | 'image_fit_width' |
      'image_fill_position_x' | 'image_fill_position_y' |
      'pexels_attributions' | 'pexels_attribution_enabled' |
      'aspect_ratio' | 'placement' | 'tactic_first_comment_enabled' |
      'reddit_destination' | 'reddit_flair_id'
    >>
  ): Promise<{ success: boolean; updated_at?: string; error?: string; queue_resumed?: boolean; queue_held?: boolean }> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/content/${itemId}`, {
        method: 'PATCH',
        headers: authJsonHeaders(),
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const error = await response.text();
        console.error(`Failed to update TendV2 content ${itemId}:`, error);
        return { success: false, error };
      }

      const data = await response.json();
      if (data.queue_resumed || data.queue_held) {
        // This edit moved the queue ticket — lifted a system hold (back
        // under the channel's character ceiling) or pushed a queued post
        // over it (held): the publish button on any mounted card for this
        // item refreshes its ticket (SmartPublishButton).
        window.dispatchEvent(new CustomEvent(QUEUE_RESUMED_EVENT, { detail: { contentId: itemId } }));
      }
      return { success: true, updated_at: data.updated_at,
               queue_resumed: !!data.queue_resumed, queue_held: !!data.queue_held };
    } catch (error) {
      console.error('Error updating TendV2 content:', error);
      return { success: false, error: String(error) };
    }
  }

  /**
   * Batch update display_order for multiple content items (drag/drop reordering)
   * @param updates - Array of { id: number, display_order: number }
   * @returns Success status with updated count
   */
  async updateContentOrder(
    updates: Array<{ id: number; display_order: number }>
  ): Promise<{ success: boolean; updated_count?: number; error?: string }> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/content/reorder`, {
        method: 'POST',
        headers: authJsonHeaders(),
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        const error = await response.text();
        console.error('Failed to reorder content:', error);
        return { success: false, error };
      }

      const data = await response.json();
      return { success: true, updated_count: data.updated_count };
    } catch (error) {
      console.error('Error reordering content:', error);
      return { success: false, error: String(error) };
    }
  }

  /**
   * Archive tended content (SuperAdmin only)
   * @param contentId - The content item ID to archive
   * @returns Success status with archived_at timestamp
   */
  async archiveContent(
    contentId: number
  ): Promise<{ success: boolean; archived_at?: string; error?: string }> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/content/${contentId}/archive`, {
        method: 'POST',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        const error = await response.text();
        console.error(`Failed to archive content ${contentId}:`, error);
        return { success: false, error };
      }

      const data = await response.json();
      return { success: true, archived_at: data.archived_at };
    } catch (error) {
      console.error('Error archiving content:', error);
      return { success: false, error: String(error) };
    }
  }

  /**
   * Unarchive (restore) tended content (SuperAdmin only)
   * @param contentId - The content item ID to unarchive
   * @returns Success status
   */
  async unarchiveContent(
    contentId: number
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/content/${contentId}/unarchive`, {
        method: 'POST',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        const error = await response.text();
        console.error(`Failed to unarchive content ${contentId}:`, error);
        return { success: false, error };
      }

      return { success: true };
    } catch (error) {
      console.error('Error unarchiving content:', error);
      return { success: false, error: String(error) };
    }
  }

  /**
   * Upload a media file (image or video) to a TendV2 content item
   * @param itemId - The content item ID
   * @param file - The file to upload (image or video)
   * @returns Success status with updated media_urls array
   */
  async uploadMediaToContent(
    itemId: number,
    file: File,
    /** 'recording' = the in-situ recorder's Save: tags the clip and stamps
        the item's take_recorded_at (Take phase 1). Default = a file from disk. */
    source: 'upload' | 'recording' = 'upload'
  ): Promise<{
    success: boolean;
    media_urls?: string[];
    uploaded_url?: string;
    media_id?: number;
    take_recorded_at?: string | null;
    error?: string;
  }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const query = source === 'recording' ? '?source=recording' : '';
      const response = await authedFetch(`${this.baseUrl}/tendv2/content/${itemId}/media/upload${query}`, {
        method: 'POST',
        headers: authOnlyHeaders(),
        body: formData,
        // Don't set Content-Type header - browser will set it with boundary
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || `Upload failed: ${response.statusText}`;
        console.error(`Failed to upload media to content ${itemId}:`, errorMessage);
        return { success: false, error: errorMessage };
      }

      const data = await response.json();
      return {
        success: true,
        media_urls: data.media_urls,
        uploaded_url: data.uploaded_url,
        media_id: data.media_id,
        take_recorded_at: data.take_recorded_at ?? null,
      };
    } catch (error) {
      console.error('Error uploading media:', error);
      return { success: false, error: String(error) };
    }
  }

  // ====================================================================
  // TAKE REQUESTS (Take phase 1)
  // ====================================================================

  /** Ask for a take on an item (the publish menu's "Request a Take").
   * Idempotent server-side; refused (409) once the brand published it.
   * Take precedes Queued: a queued / held / failed item is pulled out of
   * its channel line (`dequeued`) and queued again once recorded. */
  async requestTake(itemId: number, brandId: number): Promise<TakeFields & { dequeued: boolean }> {
    const response = await authedFetch(`${this.baseUrl}/tendv2/content/${itemId}/take-request`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify({ brand_id: brandId }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to request a take' }));
      throw new Error(error.detail || 'Failed to request a take');
    }
    const data = await response.json();
    return { ...pickTakeFields(data), dequeued: !!data.dequeued };
  }

  /** Remove the take request (either source). Not an event; a recorded
   * clip stays with the item as media. */
  async removeTakeRequest(itemId: number): Promise<TakeFields> {
    const response = await authedFetch(`${this.baseUrl}/tendv2/content/${itemId}/take-request`, {
      method: 'DELETE',
      headers: authJsonHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to remove the take request' }));
      throw new Error(error.detail || 'Failed to remove the take request');
    }
    return pickTakeFields(await response.json());
  }

  /**
   * Get both SEED and TendV2 content for display in the modal
   * Uses retry logic for TendV2 to handle server errors gracefully
   */
  async getCombinedTransformationContent(contentId: number): Promise<{
    seed: TransformationResponse[];
    tendv2: Tendv2ContentItem[];
    errors: string[];
  }> {
    const results = {
      seed: [] as TransformationResponse[],
      tendv2: [] as Tendv2ContentItem[],
      errors: [] as string[]
    };

    // Fetch both in parallel
    const [seedResult, tendv2Result] = await Promise.allSettled([
      // Fetch SEED transformations
      this.getContentTransformations(contentId),
      // Fetch TendV2 content with retry logic
      this.getTendV2ContentItems(contentId)
    ]);

    // Process SEED results
    if (seedResult.status === 'fulfilled') {
      results.seed = seedResult.value;
    } else {
      console.warn('Failed to fetch SEED transformations:', seedResult.reason);
      results.errors.push('SEED transformations unavailable');
    }

    // Process TendV2 results
    if (tendv2Result.status === 'fulfilled') {
      results.tendv2 = tendv2Result.value;
    } else {
      console.warn('Failed to fetch TendV2 content:', tendv2Result.reason);
      results.errors.push('TendV2 content unavailable');
    }

    return results;
  }

  // ====================================================================
  // OVERLAY TEMPLATE METHODS
  // ====================================================================

  // Shared list caches: in-flight dedup + short TTL. Every mounted card
  // fetches the brand template list and overlay settings on mount (dev
  // StrictMode doubles it) — ~180 identical GETs per 45-card view, which
  // saturates the browser's per-host connection pool and starves unrelated
  // requests (the Overlay Templates modal spinner). Collapse them to one.
  private static readonly LIST_TTL_MS = 30_000;
  private templateListCache = new Map<string, { ts: number; promise: Promise<OverlayTemplateResponse[]> }>();
  private overlaySettingsCache = new Map<number, { ts: number; promise: Promise<BrandOverlaySettings | null> }>();

  private invalidateTemplateListCache(): void {
    this.templateListCache.clear();
  }

  async getOverlayTemplates(brandId?: number): Promise<OverlayTemplateResponse[]> {
    const cacheKey = String(brandId ?? 'all');
    const cached = this.templateListCache.get(cacheKey);
    if (cached && Date.now() - cached.ts < TendApi.LIST_TTL_MS) {
      return cached.promise;
    }

    const promise = (async (): Promise<OverlayTemplateResponse[]> => {
      try {
        let endpoint = `/tendv2/overlay-templates`;
        if (brandId !== undefined) {
          endpoint += `?brand_id=${brandId}`;
        }

        const response = await authedFetch(`${this.baseUrl}${endpoint}`, {
          method: 'GET',
          headers: authJsonHeaders(),
        });

        if (!response.ok) {
          console.warn(`Failed to fetch overlay templates: ${response.statusText}`);
          this.templateListCache.delete(cacheKey);
          return [];
        }

        return await response.json();
      } catch (error) {
        console.warn('Error fetching overlay templates:', error);
        this.templateListCache.delete(cacheKey);
        return [];
      }
    })();

    this.templateListCache.set(cacheKey, { ts: Date.now(), promise });
    return promise;
  }

  /**
   * Get overlay templates for the template management modal.
   * Supports scope filtering for SuperAdmin tabs.
   */
  async getOverlayTemplatesForModal(
    brandId: number,
    options?: { scope?: 'global' | 'brand' | 'all'; includeHidden?: boolean }
  ): Promise<OverlayTemplateResponse[]> {
    try {
      const params = new URLSearchParams();
      params.append('brand_id', brandId.toString());

      if (options?.scope) {
        params.append('scope', options.scope);
      }
      if (options?.includeHidden) {
        params.append('include_hidden', 'true');
      }

      const response = await authedFetch(`${this.baseUrl}/tendv2/overlay-templates?${params}`, {
        method: 'GET',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        console.warn(`Failed to fetch overlay templates: ${response.statusText}`);
        return [];
      }

      return await response.json();
    } catch (error) {
      console.warn('Error fetching overlay templates for modal:', error);
      return [];
    }
  }

  /**
   * Create a new overlay template.
   * @deprecated Use createOverlayTemplateV2 for new code - slotNumber is no longer required
   */
  async createOverlayTemplate(
    slotNumber: number,
    config: OverlayTemplateConfig,
    brandId?: number
  ): Promise<OverlayTemplateResponse | null> {
    this.invalidateTemplateListCache();
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/overlay-templates`, {
        method: 'POST',
        headers: authJsonHeaders(),
        body: JSON.stringify({
          brand_id: brandId ?? null,
          slot_number: slotNumber,
          config,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to create overlay template:', errorData);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating overlay template:', error);
      return null;
    }
  }

  /**
   * Create a new overlay template (V2 - slot_number optional).
   */
  async createOverlayTemplateV2(
    config: OverlayTemplateConfig,
    options?: {
      brandId?: number | null;
      name?: string;
      description?: string;
      slotNumber?: number;  // Optional for backwards compat
    }
  ): Promise<OverlayTemplateResponse | null> {
    this.invalidateTemplateListCache();
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/overlay-templates`, {
        method: 'POST',
        headers: authJsonHeaders(),
        body: JSON.stringify({
          brand_id: options?.brandId ?? null,
          slot_number: options?.slotNumber,
          config,
          name: options?.name,
          description: options?.description,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to create overlay template:', errorData);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating overlay template:', error);
      return null;
    }
  }

  async updateOverlayTemplate(
    templateId: number,
    config: OverlayTemplateConfig,
    options?: { brand_id?: number | null; name?: string; description?: string }
  ): Promise<OverlayTemplateResponse | null> {
    this.invalidateTemplateListCache();
    try {
      const body: Record<string, unknown> = { config };
      if (options?.brand_id !== undefined) {
        body.brand_id = options.brand_id;
      }
      if (options?.name !== undefined) {
        body.name = options.name;
      }
      if (options?.description !== undefined) {
        body.description = options.description;
      }

      const response = await authedFetch(`${this.baseUrl}/tendv2/overlay-templates/${templateId}`, {
        method: 'PATCH',
        headers: authJsonHeaders(),
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        console.error(`Failed to update overlay template ${templateId}`);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating overlay template:', error);
      return null;
    }
  }

  /**
   * Set template scope (global vs brand-specific).
   * SuperAdmin only: setting brandId to null makes template global.
   */
  async setTemplateScope(
    templateId: number,
    brandId: number | null
  ): Promise<OverlayTemplateResponse | null> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/overlay-templates/${templateId}`, {
        method: 'PATCH',
        headers: authJsonHeaders(),
        body: JSON.stringify({ brand_id: brandId }),
      });

      if (!response.ok) {
        console.error(`Failed to set template scope for ${templateId}`);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Error setting template scope:', error);
      return null;
    }
  }

  async deleteOverlayTemplate(templateId: number): Promise<{ success: boolean }> {
    this.invalidateTemplateListCache();
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/overlay-templates/${templateId}`, {
        method: 'DELETE',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        console.error(`Failed to delete overlay template ${templateId}`);
        return { success: false };
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting overlay template:', error);
      return { success: false };
    }
  }

  /**
   * Soft-delete a template (sets is_hidden=true).
   * For brand templates only - global templates require hard delete.
   */
  async hideOverlayTemplate(templateId: number): Promise<{ success: boolean; deleted?: string }> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/overlay-templates/${templateId}`, {
        method: 'DELETE',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        console.error(`Failed to hide overlay template ${templateId}`);
        return { success: false };
      }

      return await response.json();
    } catch (error) {
      console.error('Error hiding overlay template:', error);
      return { success: false };
    }
  }

  /**
   * Hard-delete a template (permanent, SuperAdmin only for global).
   */
  async hardDeleteOverlayTemplate(templateId: number): Promise<{ success: boolean }> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/overlay-templates/${templateId}?hard_delete=true`, {
        method: 'DELETE',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        console.error(`Failed to hard-delete overlay template ${templateId}`);
        return { success: false };
      }

      return await response.json();
    } catch (error) {
      console.error('Error hard-deleting overlay template:', error);
      return { success: false };
    }
  }

  /**
   * Set a template as the default for its scope (global or brand).
   * For global templates: Sets as the global default.
   * For brand templates: Sets as that brand's default.
   */
  async setTemplateAsDefault(templateId: number): Promise<OverlayTemplateResponse | null> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/overlay-templates/${templateId}/set-default`, {
        method: 'POST',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        console.error(`Failed to set template ${templateId} as default`);
        return null;
      }

      const result = await response.json();
      return result.template;
    } catch (error) {
      console.error('Error setting template as default:', error);
      return null;
    }
  }

  /**
   * Update display order for multiple templates (batch update).
   */
  async updateTemplateOrder(
    updates: Array<{ id: number; display_order: number }>
  ): Promise<boolean> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/overlay-templates/reorder`, {
        method: 'POST',
        headers: authJsonHeaders(),
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        console.error('Failed to update template order');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error updating template order:', error);
      return false;
    }
  }

  /**
   * Copy a template to create a brand-specific version.
   * Used when brand users edit global templates.
   */
  async copyOverlayTemplate(
    templateId: number,
    brandId: number,
    options?: { name?: string; description?: string }
  ): Promise<OverlayTemplateResponse | null> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/overlay-templates/${templateId}/copy`, {
        method: 'POST',
        headers: authJsonHeaders(),
        body: JSON.stringify({
          brand_id: brandId,
          name: options?.name,
          description: options?.description,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to copy overlay template:', errorData);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Error copying overlay template:', error);
      return null;
    }
  }

  /**
   * Restore a soft-deleted (hidden) template.
   */
  async restoreOverlayTemplate(templateId: number): Promise<{ success: boolean }> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/overlay-templates/${templateId}/restore`, {
        method: 'POST',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        console.error(`Failed to restore overlay template ${templateId}`);
        return { success: false };
      }

      return await response.json();
    } catch (error) {
      console.error('Error restoring overlay template:', error);
      return { success: false };
    }
  }

  // ====================================================================
  // CONTENT-TYPE TEMPLATE DEFAULTS METHODS
  // ====================================================================

  async getOverlayTemplateDefaults(brandId?: number): Promise<OverlayTemplateDefaults | null> {
    try {
      const params = brandId ? `?brand_id=${brandId}` : '';
      const response = await authedFetch(`${this.baseUrl}/tendv2/overlay-template-defaults${params}`, {
        method: 'GET',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        console.warn(`Failed to fetch template defaults: ${response.statusText}`);
        return null;
      }

      const data = await response.json();
      return data.defaults;
    } catch (error) {
      console.error('Error fetching template defaults:', error);
      return null;
    }
  }

  async setOverlayTemplateDefault(
    scopeType: 'global' | 'brand',
    brandId: number | null,
    contentType: string,
    slideRole: string,
    templateId: number | null
  ): Promise<{ success: boolean; defaults?: OverlayTemplateDefaults }> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/overlay-template-defaults`, {
        method: 'PUT',
        headers: authJsonHeaders(),
        body: JSON.stringify({
          scope_type: scopeType,
          brand_id: brandId,
          content_type: contentType,
          slide_role: slideRole,
          template_id: templateId,
        }),
      });

      if (!response.ok) {
        console.error(`Failed to set template default: ${response.statusText}`);
        return { success: false };
      }

      const data = await response.json();
      return { success: true, defaults: data.defaults };
    } catch (error) {
      console.error('Error setting template default:', error);
      return { success: false };
    }
  }

  async clearOverlayTemplateDefault(
    scopeType: 'global' | 'brand',
    brandId: number | null,
    contentType: string,
    slideRole: string
  ): Promise<{ success: boolean }> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/overlay-template-defaults`, {
        method: 'DELETE',
        headers: authJsonHeaders(),
        body: JSON.stringify({
          scope_type: scopeType,
          brand_id: brandId,
          content_type: contentType,
          slide_role: slideRole,
        }),
      });

      if (!response.ok) {
        console.error(`Failed to clear template default: ${response.statusText}`);
        return { success: false };
      }

      return { success: true };
    } catch (error) {
      console.error('Error clearing template default:', error);
      return { success: false };
    }
  }

  async getRecentContentImage(brandId: number): Promise<string | null> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/brands/${brandId}/recent-content-image`, {
        method: 'GET',
        headers: authJsonHeaders(),
      });

      if (!response.ok) return null;

      const data = await response.json();
      return data.image_url || null;
    } catch (error) {
      console.error('Error fetching recent content image:', error);
      return null;
    }
  }

  // ====================================================================
  // BRAND OVERLAY SETTINGS METHODS
  // ====================================================================

  /**
   * Resolve a tactic's default template for the Editorial ▾ / tactic modal
   * slot. brandId null = Universal scope (global rows only). postType is the
   * tactic's frontmatter post_type; it picks which post-type slot is the
   * fallback shown as "inherited".
   */
  async getTacticTemplateDefault(
    platform: string,
    tacticId: string,
    brandId: number | null,
    postType: string,
    /** The tactic's frontmatter take switch — a single-post take inherits the Video default. */
    take: boolean = false
  ): Promise<TacticTemplateDefaultResolution | null> {
    try {
      const params = new URLSearchParams({ platform, tactic_id: tacticId, post_type: postType || 'single' });
      if (brandId) params.append('brand_id', String(brandId));
      if (take) params.append('take', 'true');
      const response = await authedFetch(`${this.baseUrl}/tendv2/overlay-template-defaults/tactic?${params}`, {
        method: 'GET',
        headers: authJsonHeaders(),
      });
      if (!response.ok) {
        console.warn(`Failed to fetch tactic template default: ${response.statusText}`);
        return null;
      }
      const data = await response.json();
      return data.resolution ?? null;
    } catch (error) {
      console.error('Error fetching tactic template default:', error);
      return null;
    }
  }

  /** Set (templateId) or clear (null) a tactic's default template for a scope. Saves immediately. */
  async setTacticTemplateDefault(
    scopeType: 'global' | 'brand',
    brandId: number | null,
    platform: string,
    tacticId: string,
    postType: string,
    templateId: number | null,
    take: boolean = false
  ): Promise<{ success: boolean; action?: string; resolution?: TacticTemplateDefaultResolution; error?: string }> {
    try {
      const response = await authedFetch(`${this.baseUrl}/tendv2/overlay-template-defaults/tactic`, {
        method: 'PUT',
        headers: authJsonHeaders(),
        body: JSON.stringify({
          scope_type: scopeType,
          brand_id: brandId,
          platform,
          tactic_id: tacticId,
          post_type: postType || 'single',
          take,
          template_id: templateId,
        }),
      });
      if (!response.ok) {
        let detail = response.statusText;
        try { detail = (await response.json())?.detail || detail; } catch { /* keep statusText */ }
        console.error(`Failed to set tactic template default: ${detail}`);
        return { success: false, error: detail };
      }
      const data = await response.json();
      return { success: true, action: data.action, resolution: data.resolution };
    } catch (error) {
      console.error('Error setting tactic template default:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Request failed' };
    }
  }

  async getBrandOverlaySettings(brandId: number): Promise<BrandOverlaySettings | null> {
    const cached = this.overlaySettingsCache.get(brandId);
    if (cached && Date.now() - cached.ts < TendApi.LIST_TTL_MS) {
      return cached.promise;
    }

    const promise = (async (): Promise<BrandOverlaySettings | null> => {
      try {
        const response = await authedFetch(`${this.baseUrl}/tendv2/brands/${brandId}/overlay-settings`, {
          method: 'GET',
          headers: authJsonHeaders(),
        });

        if (!response.ok) {
          console.warn(`Failed to fetch brand overlay settings: ${response.statusText}`);
          this.overlaySettingsCache.delete(brandId);
          return null;
        }

        return await response.json();
      } catch (error) {
        console.warn('Error fetching brand overlay settings:', error);
        this.overlaySettingsCache.delete(brandId);
        return null;
      }
    })();

    this.overlaySettingsCache.set(brandId, { ts: Date.now(), promise });
    return promise;
  }

  async updateBrandOverlaySettings(
    // Any write invalidates the settings cache so the next read is fresh
    brandId: number,
    settings: Partial<Omit<BrandOverlaySettings, 'brand_id'>>
  ): Promise<BrandOverlaySettings | null> {
    try {
      this.overlaySettingsCache.delete(brandId);
      const response = await authedFetch(`${this.baseUrl}/tendv2/brands/${brandId}/overlay-settings`, {
        method: 'PATCH',
        headers: authJsonHeaders(),
        body: JSON.stringify(settings),
      });

      if (!response.ok) {
        console.error('Failed to update brand overlay settings');
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating brand overlay settings:', error);
      return null;
    }
  }

  /** Copy a brand original-content image to S3 as the template-preview
      default (Design modal easter egg). Returns the stored S3 URL. */
  async setBrandTemplatePreviewImage(brandId: number, url: string): Promise<string | null> {
    try {
      this.overlaySettingsCache.delete(brandId);
      const response = await authedFetch(`${this.baseUrl}/tendv2/brands/${brandId}/template-preview-image`, {
        method: 'POST',
        headers: authJsonHeaders(),
        body: JSON.stringify({ url }),
      });
      if (!response.ok) {
        console.error('Failed to set template preview image');
        return null;
      }
      const data = await response.json();
      return data.template_preview_image_url || null;
    } catch (error) {
      console.error('Error setting template preview image:', error);
      return null;
    }
  }

  /** Clear the template-preview default: previews revert to the brand color. */
  async clearBrandTemplatePreviewImage(brandId: number): Promise<boolean> {
    try {
      this.overlaySettingsCache.delete(brandId);
      const response = await authedFetch(`${this.baseUrl}/tendv2/brands/${brandId}/template-preview-image`, {
        method: 'DELETE',
        headers: authJsonHeaders(),
      });
      return response.ok;
    } catch (error) {
      console.error('Error clearing template preview image:', error);
      return false;
    }
  }

  /** Universal layer: copy an image from ANY brand's pool to S3 as the
      default template preview for brands without their own pick. */
  async setUniversalTemplatePreviewImage(url: string): Promise<string | null> {
    try {
      this.overlaySettingsCache.clear(); // universal affects every brand
      const response = await authedFetch(`${this.baseUrl}/tendv2/settings/universal-template-preview-image`, {
        method: 'POST',
        headers: authJsonHeaders(),
        body: JSON.stringify({ url }),
      });
      if (!response.ok) {
        console.error('Failed to set universal template preview image');
        return null;
      }
      const data = await response.json();
      return data.universal_template_preview_image_url || null;
    } catch (error) {
      console.error('Error setting universal template preview image:', error);
      return null;
    }
  }

  async clearUniversalTemplatePreviewImage(): Promise<boolean> {
    try {
      this.overlaySettingsCache.clear();
      const response = await authedFetch(`${this.baseUrl}/tendv2/settings/universal-template-preview-image`, {
        method: 'DELETE',
        headers: authJsonHeaders(),
      });
      return response.ok;
    } catch (error) {
      console.error('Error clearing universal template preview image:', error);
      return false;
    }
  }

  /** Universal color palette (gateway-served; superadmin write). Null = failure. */
  async getUniversalColorPalette(): Promise<{ colors: string[] } | null> {
    try {
      const response = await authedFetch(`${this.baseUrl}/settings/universal-color-palette`, {
        headers: authJsonHeaders(),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Error getting universal color palette:', error);
      return null;
    }
  }

  async updateUniversalColorPalette(colors: string[]): Promise<{ colors: string[] } | null> {
    try {
      const response = await authedFetch(`${this.baseUrl}/settings/universal-color-palette`, {
        method: 'PUT',
        headers: authJsonHeaders(),
        body: JSON.stringify({ colors }),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Error updating universal color palette:', error);
      return null;
    }
  }

  /** Universal color roles (gateway-served; superadmin write; whole-map replace). Null = failure. */
  async getUniversalColorRoles(): Promise<{ roles: Record<string, string> } | null> {
    try {
      const response = await authedFetch(`${this.baseUrl}/settings/universal-color-roles`, {
        headers: authJsonHeaders(),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Error getting universal color roles:', error);
      return null;
    }
  }

  async updateUniversalColorRoles(roles: Record<string, string>): Promise<{ roles: Record<string, string> } | null> {
    try {
      const response = await authedFetch(`${this.baseUrl}/settings/universal-color-roles`, {
        method: 'PUT',
        headers: authJsonHeaders(),
        body: JSON.stringify({ roles }),
      });
      if (!response.ok) return null;
      this.overlaySettingsCache.clear();
      return await response.json();
    } catch (error) {
      console.error('Error updating universal color roles:', error);
      return null;
    }
  }

  /** Universal channel aspect map (gateway-served; superadmin write). Null = failure. */
  async getUniversalSafeZones(): Promise<{ safe_zones: Record<string, Record<string, any>>; can_edit: boolean } | null> {
    try {
      const response = await authedFetch(`${this.baseUrl}/settings/universal-safe-zones`, {
        headers: authJsonHeaders(),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch universal safe zones:', error);
      return null;
    }
  }

  /** Sparse write: only the channel/placement entries sent are stored; the
      response is the merged map (seeds + stored). Universal admin only. */
  async updateUniversalSafeZones(
    safeZones: Record<string, Record<string, any>>
  ): Promise<{ safe_zones: Record<string, Record<string, any>> } | null> {
    try {
      const response = await authedFetch(`${this.baseUrl}/settings/universal-safe-zones`, {
        method: 'PUT',
        headers: authJsonHeaders(),
        body: JSON.stringify({ safe_zones: safeZones }),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Failed to update universal safe zones:', error);
      return null;
    }
  }

  async getUniversalChannelAspect(): Promise<{ channel_aspect: Record<string, string> } | null> {
    try {
      const response = await authedFetch(`${this.baseUrl}/settings/universal-channel-aspect`, {
        headers: authJsonHeaders(),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Error getting universal channel aspect:', error);
      return null;
    }
  }

  async updateUniversalChannelAspect(
    channelAspect: Record<string, string>
  ): Promise<{ channel_aspect: Record<string, string> } | null> {
    try {
      const response = await authedFetch(`${this.baseUrl}/settings/universal-channel-aspect`, {
        method: 'PUT',
        headers: authJsonHeaders(),
        body: JSON.stringify({ channel_aspect: channelAspect }),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Error updating universal channel aspect:', error);
      return null;
    }
  }

  /** Superadmin: replace the FULL universal editorial cadence (channels for
      all 13 platforms with every field + order permutation). */
  async updateUniversalEditorialCadence(
    value: { channels: Record<string, any>; order: string[] }
  ): Promise<{ editorial_cadence: { channels: Record<string, any>; order: string[] } } | null> {
    try {
      const response = await authedFetch(`${this.baseUrl}/settings/universal-editorial-cadence`, {
        method: 'PUT',
        headers: authJsonHeaders(),
        body: JSON.stringify({ editorial_cadence: value }),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Error updating universal editorial cadence:', error);
      return null;
    }
  }

  /** Superadmin: replace the universal link settings layer (sparse — absent
      fields inherit code defaults). */
  async updateUniversalLinkSettings(
    value: Record<string, any>
  ): Promise<{ link_settings: Record<string, any> } | null> {
    try {
      const response = await authedFetch(`${this.baseUrl}/settings/universal-link-settings`, {
        method: 'PUT',
        headers: authJsonHeaders(),
        body: JSON.stringify({ link_settings: value }),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Error updating universal link settings:', error);
      return null;
    }
  }

  /** Universal notification settings (token MASKED — set/last4 only) plus
      the Event Tracking catalog for the settings UI. CS/superadmin. */
  async getUniversalNotificationSettings(): Promise<{
    notification_settings: Record<string, any>;
    catalog: {
      categories: Record<string, string[]>;
      pipes: string[];
      external_eligible: string[];
      high_frequency: string[];
    };
  } | null> {
    try {
      const response = await authedFetch(`${this.baseUrl}/settings/universal-notification-settings`, {
        headers: authJsonHeaders(),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Error loading universal notification settings:', error);
      return null;
    }
  }

  /** Replace the universal notification layer. Token semantics: key absent
      = keep stored token, null = clear, string = replace (superadmin only). */
  async updateUniversalNotificationSettings(
    value: Record<string, any>
  ): Promise<{ notification_settings: Record<string, any> } | null> {
    try {
      const response = await authedFetch(`${this.baseUrl}/settings/universal-notification-settings`, {
        method: 'PUT',
        headers: authJsonHeaders(),
        body: JSON.stringify({ notification_settings: value }),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Error updating universal notification settings:', error);
      return null;
    }
  }

  /** The IANA zone the background publisher interprets cadence HH:MM
      windows in (platform-wide; superadmin-editable). */
  async getSchedulerTimezone(): Promise<string | null> {
    try {
      const response = await authedFetch(`${this.baseUrl}/settings/scheduler-timezone`, {
        headers: authJsonHeaders(),
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data.timezone || null;
    } catch (error) {
      console.error('Error fetching scheduler timezone:', error);
      return null;
    }
  }

  async renderOverlay(
    itemId: number,
    overlayConfig: OverlayTemplateConfig,
    logoUrl: string,
    logoKey: string,
    logoDisplayMode: string,
    brandName: string,
    sync: boolean = false,
    overlayText?: { title?: string; subheader?: string; body?: string; category?: string },
    brandSettings?: { categoryBorderRadius?: number; categoryBgColor?: string },
  ): Promise<{ success: boolean; status?: string; item_id?: number; rendered_url?: string; error?: string }> {
    try {
      const url = `${this.baseUrl}/tendv2/content/${itemId}/render${sync ? '?sync=true' : ''}`;
      const response = await authedFetch(url, {
        method: 'POST',
        headers: authJsonHeaders(),
        body: JSON.stringify({
          overlay_config: overlayConfig,
          logo_url: logoUrl,
          logo_key: logoKey,
          logo_display_mode: logoDisplayMode,
          brand_name: brandName,
          overlay_text: overlayText,
          category_border_radius: brandSettings?.categoryBorderRadius,
          category_bg_color: brandSettings?.categoryBgColor,
          image_fit_mode: overlayConfig.imageFitMode,
          image_fit_anchor: overlayConfig.imageFitAnchor,
          image_fit_width: overlayConfig.imageFitWidth,
          image_fill_position_x: overlayConfig.imageFillPositionX,
          image_fill_position_y: overlayConfig.imageFillPositionY,
        }),
      });

      if (!response.ok) {
        const err = await response.text();
        console.error(`Failed to render overlay for item ${itemId}:`, err);
        return { success: false, error: err };
      }

      return await response.json();
    } catch (error) {
      console.error('Error rendering overlay:', error);
      return { success: false, error: String(error) };
    }
  }

  /**
   * Render a single carousel slide's overlay server-side via Playwright.
   * Used by the preview button when in carousel mode (so the preview shows
   * what the active slide actually looks like, not the parent content's
   * overlay). Also used implicitly during publish via the backend's
   * _render_carousel_slides batch helper — this method is just for the
   * manual UI preview path.
   *
   * The slide endpoint resolves brand info, logo, template, etc. from the
   * slide's content_id → transformation → brand chain server-side, so the
   * frontend doesn't need to pass any of that.
   */
  async renderSlideOverlay(
    slideId: number,
    forceRerender: boolean = true,
  ): Promise<{
    slide_id?: number;
    rendered_overlay_url?: string;
    rendered_video_url?: string | null;
    status?: string;
    message?: string;
    success: boolean;
    error?: string;
  }> {
    try {
      const url = `${this.baseUrl}/tendv2/slides/${slideId}/render`;
      const response = await authedFetch(url, {
        method: 'POST',
        headers: authJsonHeaders(),
        body: JSON.stringify({ force_rerender: forceRerender }),
      });

      if (!response.ok) {
        const err = await response.text();
        console.error(`Failed to render slide overlay for slide ${slideId}:`, err);
        return { success: false, error: err };
      }

      const data = await response.json();
      return {
        ...data,
        success: data.status === 'success',
      };
    } catch (error) {
      console.error('Error rendering slide overlay:', error);
      return { success: false, error: String(error) };
    }
  }

  // ----- Design Elements library -----

  async listDesignElements(brandId?: number | null): Promise<DesignElementLibraryItem[]> {
    const params = new URLSearchParams();
    if (brandId != null) params.set('brand_id', String(brandId));
    const token = getStoredToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = formatAuthHeader(token);
    try {
      const res = await authedFetch(`${API_BASE_URL}/tend/design-elements?${params}`, { headers });
      if (!res.ok) {
        console.warn('listDesignElements failed:', res.status);
        return [];
      }
      const data = await res.json();
      return data.elements || [];
    } catch (e) {
      console.error('listDesignElements error:', e);
      return [];
    }
  }

  async uploadDesignElement(args: {
    file: File;
    scope: 'universal' | 'brand';
    brandId?: number;
    name?: string;
  }): Promise<DesignElementLibraryItem | null> {
    const fd = new FormData();
    fd.append('file', args.file);
    fd.append('scope', args.scope);
    if (args.brandId != null) fd.append('brand_id', String(args.brandId));
    if (args.name) fd.append('name', args.name);
    const token = getStoredToken();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = formatAuthHeader(token);
    try {
      const res = await authedFetch(`${API_BASE_URL}/tend/design-elements/upload`, {
        method: 'POST',
        headers,
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed: ${res.statusText}`);
      }
      return await res.json();
    } catch (e) {
      console.error('uploadDesignElement error:', e);
      throw e;
    }
  }

  async deleteDesignElement(id: number): Promise<boolean> {
    const token = getStoredToken();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = formatAuthHeader(token);
    try {
      const res = await authedFetch(`${API_BASE_URL}/tend/design-elements/${id}`, {
        method: 'DELETE',
        headers,
      });
      return res.ok;
    } catch (e) {
      console.error('deleteDesignElement error:', e);
      return false;
    }
  }
}

export interface BloomPublishResponse {
  success: boolean;
  post_uri?: string;
  post_url?: string;
  message?: string;
  error?: string;
}

// Tendv2 Interfaces
export interface TacticSelection {
  platform: string;
  tactic_id: string;
  custom_prompt?: string;
  metadata_override?: any;
  /** Take phase 1, per tactic per run (the picker's row toggle): true = a
      take this run even if the tactic isn't; false = not a take this run
      even if it is; omitted = the tactic's own switch decides. */
  take?: boolean;
}

export interface Tendv2TransformRequest {
  content_id: number;
  brand_id?: number; // Preferred
  brand_slug?: string; // For backward compatibility
  execution_mode: 'branch' | 'prune';
  selected_tactics?: TacticSelection[]; // Only for prune mode
  llm_model?: string; // Optional LLM model override
  pre_prompt?: string; // Optional pre-prompt override
  parallel_execution?: boolean;
  use_cache?: boolean;
  mock_llm?: boolean;
  /** Take phase 1, per-run: stamp every item this run produces as a take
      request without changing the tactics' frontmatter switch. */
  take?: boolean;
}

export interface Tendv2TacticOption {
  id: string;
  platform: string;
  name: string;
  description?: string;
  tone?: string;
  estimated_tokens?: number;
  category?: string;
  priority?: string;
  effectiveness?: number;
  objective?: string;
  template?: string;
  examples?: string[];
  is_custom?: boolean;
  brand_slug?: string;
  // Tactic settings from frontmatter
  caption_enabled?: boolean;
  image_mode?: string;
  link_enabled?: boolean;
  hashtags_enabled?: boolean;
  first_comment_enabled?: boolean;
  thread_enabled?: boolean;
  /** Frontmatter `take: true` — every tend from this tactic is a take request (Take phase 1). */
  take?: boolean;
  readiness?: string;
}

export interface Tendv2TacticExecution {
  id: number;
  tactic_id: string;
  platform: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  start_time?: string;
  end_time?: string;
  execution_time_ms?: number;
  tokens_used?: number;
  cost_estimate?: number;
  error_message?: string;
  retry_count?: number;
  content?: Tendv2Content;
}

export interface Tendv2Content {
  id: number;
  platform: string;
  tactic_id: string;
  format: string;
  primary_text: string;
  secondary_text?: string;
  hashtags?: string[];
  mentions?: string[];
  character_count: number;
  word_count: number;
  media_suggestions?: string[];
  metadata?: Record<string, any>;
}

export interface Tendv2TransformResponse {
  id: number;
  content_id: number;
  brand_id?: number;
  execution_mode: 'branch' | 'prune';
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'partial';
  start_time: string;
  end_time?: string;
  total_execution_time_ms?: number;
  total_tactics: number;
  completed_tactics: number;
  failed_tactics: number;
  total_tokens_used?: number;
  total_cost?: number;
  parallel_execution: boolean;
  cache_hits?: number;
  error_message?: string;
  tactic_executions: Tendv2TacticExecution[];
}

// Vizard video clip metadata
export interface VizardMetadata {
  videoId?: number;
  viralScore?: string | number;
  viralReason?: string;
  relatedTopic?: string;
  clipEditorUrl?: string;
  durationMs?: number;
}

// Platform metadata structure
export interface PlatformMetadata {
  vizard?: VizardMetadata;
  [key: string]: any;
}

// TendV2 Content interfaces for display
export interface Tendv2ContentItem {
  id: number;
  transformation_id: number | null;
  tactic_execution_id?: number | null;
  vizard_transformation_id?: number | null;
  platform: string;
  tactic_id: string;
  content_format?: string;  // 'post', 'thread', 'story', 'reel', 'video_clip'
  format: string;
  primary_text: string;
  secondary_text?: string;
  headline?: string;
  transcript?: string;  // Video clip transcript
  hashtags?: string[];
  mentions?: string[];
  character_count: number;
  word_count: number;
  estimated_read_time_seconds?: number;  // Duration in seconds for video clips
  engagement_score?: number;  // Viral score for video clips
  primary_image_url?: string | null;
  rendered_overlay_url?: string | null;
  media_urls?: string[];
  /** Content Media Gallery: this post's media order was reordered by hand (else it follows the original's). */
  media_order_custom?: boolean;
  media_suggestions?: string[];
  media_generation_prompt?: string;
  metadata?: Record<string, any>;
  platform_metadata?: PlatformMetadata;  // Vizard and other platform-specific data
  created_at: string;
  updated_at?: string;
  // Source article fields (for overlay editing)
  source_title?: string;
  source_subhead?: string;
  source_author?: string;
  source_url?: string;
  source_content_id?: number;
  source_section?: string;
  source_category?: string;
  // Tactic settings (from frontmatter configuration)
  tactic_image_mode?: string;           // 'single', 'carousel', 'generated'
  tactic_caption_enabled?: boolean;
  tactic_link_enabled?: boolean;
  tactic_hashtags_enabled?: boolean;
  tactic_first_comment_enabled?: boolean;
  tactic_thread_enabled?: boolean;
  tactic_readiness?: string;            // 'beta', 'production', 'experimental'
  // Title overlay settings
  title_font_size?: number;             // Font size in pixels for overlay title
  // Active overlay template
  overlay_template_id?: number | null;  // ID of active overlay template for this card
  // Dummy template (unsaved overlay changes)
  overlay_config?: OverlayTemplateConfig | null;
  // AI-generated publishing warnings
  publish_warnings?: string[] | string; // Array of warnings or JSON string
  // Per-content editorial choices (independent of template/overlay_config)
  caption_mode_enabled?: boolean | null;  // True = use AI text, False = use source text
  overlay_logo_url?: string | null;       // Selected logo URL for overlay
  overlay_logo_key?: string | null;       // Logo key (e.g. 'icon_white', 'wordmark')
  overlay_logo_display_mode?: string | null; // 'icon_only', 'name_only', 'icon_and_name'
  // Image fit/fill mode
  image_fit_mode?: string | null;    // 'fill' or 'fit'
  image_fit_anchor?: string | null;  // 'top', 'center', 'bottom'
  image_fit_width?: number | null;   // 50-100 percentage
  image_fill_position_x?: number | null;  // 0-100, fill-mode crop X
  image_fill_position_y?: number | null;  // 0-100, fill-mode crop Y
  /** Channel aspect bucket ('1:1','4:5','3:4','2:3','9:16','16:9'), stamped at
      creation; null = resolve from brand/universal maps. Content-level —
      governs all carousel slides. */
  aspect_ratio?: string | null;
  // AI-generated caption fields (when tactic has caption: true)
  generated_title?: string;     // AI-generated overlay title (50-80 chars)
  generated_subheader?: string; // AI-generated overlay subheader (80-150 chars)
  generated_body?: string;      // AI-generated overlay body paragraph (unbounded)
  // Post title for title-bearing channels (reddit/pinterest/shorts/quora).
  // Seeded once from generated_title; independently editable afterwards.
  post_title?: string;
  // Channel surface (snapchat: story/saved_story/spotlight). null =
  // channel default; only meaningful for PLACEMENTS_BY_CHANNEL channels.
  placement?: string | null;
  // Reddit target ('golf' / 'u_name'; null = connected profile) + flair
  reddit_destination?: string | null;
  reddit_flair_id?: string | null;
  // Spoken script for the recording teleprompter (single-post scope; carousels store per-slide)
  script?: string;
  // Take (phase 1): a request to record a to-camera video with the
  // teleprompter — a flag orthogonal to queued/published. null
  // take_requested_at = not a take. Written by requestTake /
  // removeTakeRequest; take_recorded_at lands when the recorder's Save
  // uploads with source=recording (utils/takeState.ts reads these).
  take_requested_at?: string | null;
  take_source?: 'tactic' | 'manual' | null;
  take_requested_by?: string | null;
  take_brand_id?: number | null;
  take_recorded_at?: string | null;
  // Pexels photo attribution (map of URL -> attribution text)
  pexels_attributions?: Record<string, string>;  // { "url": "Photo by X on Pexels" }
  pexels_attribution_enabled?: boolean; // Toggle to include in post
  // Display ordering for drag/drop reordering
  display_order?: number;
  // Archive functionality (SuperAdmin only)
  is_archived?: boolean;
  archived_at?: string | null;
  // Carousel/Video Series fields
  content_mode?: 'single' | 'carousel' | 'video_series';
  slide_count?: number;
  slides?: Array<{
    id: number;
    slide_number: number;
    media_url?: string;
    media_type?: 'image' | 'video';
    title?: string;
    subheader?: string;
    generated_body?: string;
    script?: string;
    slide_text?: string;  // DEPRECATED: prefer generated_body
    duration_seconds?: number;
    rendered_overlay_url?: string;
    rendered_video_url?: string;
  }>;
}

export interface Tendv2ContentResponse {
  success: boolean;
  items?: Tendv2ContentItem[];
  items_by_platform?: {
    [platform: string]: Tendv2ContentItem[];
  };
  error?: string;
  count?: number;
  content_id?: number;
  platforms?: string[];
}

export const tendApi = new TendApi();

// Design-element library catalog row.
export interface DesignElementLibraryItem {
  id: number;
  brand_id: number | null;
  is_universal: boolean;
  name: string;
  s3_key: string;
  s3_url: string;
  mime_type: string;
  width_px: number | null;
  height_px: number | null;
  aspect_ratio: number;
  created_by: number | null;
  created_at: string | null;
}

// One placed design element on an overlay (lives inside overlay_config).
// AUTO-ANCHORING:
//   When both xAnchor + yAnchor are present, xPct/yPct are offsets from the
//   anchored edge in % of canvas WIDTH (NOT height) — this keeps elements
//   visually pinned across aspect-ratio changes (e.g. 4:5 ↔ 1:1 on the same
//   width). Drag-end auto-picks the anchor based on which 3x3 cell the
//   element's center falls into.
//
//   - xAnchor='left'   → xPct = left-edge → center, % of width
//   - xAnchor='right'  → xPct = right-edge → center, % of width
//   - xAnchor='center' → xPct = signed offset from horizontal centerline, % of width
//   - yAnchor='top'    → yPct = top-edge → center, % of width
//   - yAnchor='bottom' → yPct = bottom-edge → center, % of width
//   - yAnchor='middle' → yPct = signed offset from vertical centerline, % of width
//
//   When anchors are absent (legacy placements), xPct = % of width, yPct =
//   % of HEIGHT (both center-relative). New edits always save the anchored
//   format; legacy placements migrate on first drag.
export type DesignElementXAnchor = 'left' | 'center' | 'right';
export type DesignElementYAnchor = 'top' | 'middle' | 'bottom';

// DEPTH (Depth Ordering of design element, 2026-10-08): each placement is
// either in front of or behind the set elements (logo / title / subheader /
// body / category). Absent = 'front', so every placement made before the
// field existed keeps its look; the picker stamps 'back' on new ones.
// "Behind" still sits above the gradient and the border. Read through
// utils/designElementLayers.ts, never by hand.
export type DesignElementDepth = 'front' | 'back';

export interface PlacedDesignElement {
  instanceId: string;     // uuid; React key + selection target
  elementId: number;      // FK to TendV2DesignElement.id
  url: string;            // denormalized public S3 URL (renderers use this)
  xAnchor?: DesignElementXAnchor;
  yAnchor?: DesignElementYAnchor;
  xPct: number;           // see anchor docs above
  yPct: number;           // see anchor docs above
  widthPct: number;       // 0-100, % of overlay width
  rotation: number;       // degrees, -180..180
  aspectRatio: number;    // native width/height (drives rendered height)
  depth?: DesignElementDepth; // absent = 'front' (see above)
  flipX?: boolean;        // mirror left↔right in its own frame; absent = false
  opacity?: number;       // 0-100; absent = 100 (solid)
}

export const MAX_DESIGN_ELEMENTS_PER_OVERLAY = 20;

// Overlay Template types
export interface OverlayTemplateConfig {
  // Visibility toggles
  showOverlayLogo: boolean;
  showOverlayTitle: boolean;
  showOverlaySubheader: boolean;  // Default: false
  showOverlayCategory: boolean;   // Default: false
  showOverlayGradient: boolean;
  // Positions
  logoPosition: string;
  titlePosition: string;
  subheaderPosition: string;
  categoryPosition: string;
  // Sizes
  titleFontSize: number;
  subheaderFontSize: number;
  logoScale: number;
  /** Category pill size factor (1 = the standard pill); drawn with CSS zoom like the logo. */
  categoryScale?: number;
  /**
   * Space below each set element to the next one (or the safe edge), in
   * reference px at width 360, measured between the VISIBLE boxes (glyph box,
   * pill, logo). Null/absent = 8. Never negative: set elements cannot overlap.
   * Replaces the old vertical offset.
   */
  logoGap?: number | null;
  titleGap?: number | null;
  subheaderGap?: number | null;
  bodyGap?: number | null;
  categoryGap?: number | null;
  categoryBgColor: string;
  /** Category pill corner rounding (0=square, 100=pill). Per-config
      override of the brand default (resolution: content > template >
      brand); absent = inherit brand. */
  categoryBorderRadius?: number;
  // Gradient controls
  gradientDirection?: 'up' | 'down' | 'full';
  gradientColor?: string;
  gradientOpacity?: number;  // 0-100
  // Title background
  titleBgEnabled?: boolean;
  titleBgColor?: string;
  titleBgOpacity?: number;  // 0-100
  titleTextColor?: string;
  // Subheader background
  subheaderBgEnabled?: boolean;
  subheaderBgColor?: string;
  subheaderBgOpacity?: number;  // 0-100
  subheaderTextColor?: string;
  // Body text element (third overlay text element — paragraph-length).
  // Renders the slide's generated_body (or content's generated_body in single mode).
  showOverlayBody?: boolean;
  bodyPosition?: string;
  bodyFontSize?: number;
  bodyTextColor?: string;
  bodyBgEnabled?: boolean;
  bodyBgColor?: string;
  bodyBgOpacity?: number;  // 0-100
  // Border (behind text, above gradient) — CATEGORY-LEVEL border around image
  borderEnabled?: boolean;
  borderColor?: string;
  borderThickness?: number;  // pixels

  // Design Elements: PNG/SVG library assets placed canvas-style, each in
  // front of or behind the set elements (PlacedDesignElement.depth). Cap of
  // 20 enforced client + server side.
  designElements?: PlacedDesignElement[];

  // Per-text-element styling (Title)
  titleBold?: boolean;
  titleItalic?: boolean;
  titleUnderline?: boolean;
  titleStrikethrough?: boolean;
  titleShadow?: boolean;  // drop shadow behind text; absent = ON (legacy publish look)
  titleTextCase?: 'none' | 'uppercase' | 'lowercase' | 'titlecase';
  titleBorderEnabled?: boolean;
  titleBorderColor?: string;
  titleBorderThickness?: number;  // pixels at reference width 360
  titleBorderRadius?: number;     // pixels at reference width 360
  titleWidth?: number;            // percentage 0-100, default 100
  titleWidthOffset?: number;      // percentage 0-100; for left-anchor = left margin, right-anchor = right margin

  // Per-text-element styling (Subheader)
  subheaderBold?: boolean;
  subheaderItalic?: boolean;
  subheaderUnderline?: boolean;
  subheaderStrikethrough?: boolean;
  subheaderShadow?: boolean;
  subheaderTextCase?: 'none' | 'uppercase' | 'lowercase' | 'titlecase';
  subheaderBorderEnabled?: boolean;
  subheaderBorderColor?: string;
  subheaderBorderThickness?: number;
  subheaderBorderRadius?: number;
  subheaderWidth?: number;
  subheaderWidthOffset?: number;

  // Per-text-element styling (Body)
  bodyBold?: boolean;
  bodyItalic?: boolean;
  bodyUnderline?: boolean;
  bodyStrikethrough?: boolean;
  bodyShadow?: boolean;
  bodyTextCase?: 'none' | 'uppercase' | 'lowercase' | 'titlecase';
  bodyBorderEnabled?: boolean;
  bodyBorderColor?: string;
  bodyBorderThickness?: number;
  bodyBorderRadius?: number;
  bodyWidth?: number;
  bodyWidthOffset?: number;

  // Font Library — per-element font family (font-library family SLUG, e.g.
  // 'playfair-display'). Absent/undefined = inherit up the cascade
  // (template → brand default → universal default → Inter). Never persist
  // null/'' — JSON.stringify must drop unset keys or inheritance breaks.
  titleFontFamily?: string;
  subheaderFontFamily?: string;
  bodyFontFamily?: string;
  categoryFontFamily?: string;

  // Per-element letter spacing (integer percent, rendered as pct/100 em) and
  // line height (unitless multiplier). Absent/undefined = default (letter
  // spacing 0, line height Auto = each surface's built-in leading). Sparse
  // keys — JSON.stringify must drop unset keys so defaults keep applying.
  titleLetterSpacing?: number;
  subheaderLetterSpacing?: number;
  bodyLetterSpacing?: number;
  titleLineHeight?: number;
  subheaderLineHeight?: number;
  bodyLineHeight?: number;

  // Logo selection (for curated content - may differ from brand default)
  logoUrl?: string;
  logoKey?: string;
  logoDisplayMode?: string;
  // Caption mode: true = use AI-generated text, false = use source text
  captionModeEnabled?: boolean;
  // Image fit/fill mode
  imageFitMode?: 'fill' | 'fit' | string;
  imageFitAnchor?: 'top' | 'center' | 'bottom' | string;
  imageFitWidth?: number;  // 50-100
  imageFillPositionX?: number;  // 0-100, fill-mode crop X
  imageFillPositionY?: number;  // 0-100, fill-mode crop Y
}

export interface OverlayTemplateResponse {
  id: number;
  brand_id: number | null;
  is_global: boolean;  // true if brand_id is null (global template)
  slot_number: number | null;  // nullable for unlimited templates
  config: OverlayTemplateConfig;
  name: string | null;
  description: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  // Template management fields
  is_hidden?: boolean;
  source_template_id?: number | null;  // Tracks global→brand copies
  display_order?: number | null;
  is_default?: boolean;  // Default template for its scope (global or brand)
}

// Brand overlay settings
export interface RedditFlair {
  id: string;
  text: string;
  textEditable: boolean;
}

export interface RedditDestination {
  name: string;
  kind: 'subreddit';
  flairs: RedditFlair[];
  added_at?: string;
}

export interface BrandOverlaySettings {
  brand_id: number;
  category_border_radius: number;  // 0-100, pill to square
  category_bg_color: string;
  default_overlay_template_id: number | null;
  // Auto-transcribe new podcasts/videos at ingest (per-brand cost control)
  auto_transcribe: boolean;
  // Auto Tend: the agent tends new content under the intake playbook (agent phase 4)
  auto_tend: boolean;
  // Auto Queue: what Auto Tend made is queued to its channel line (agent phase 4)
  auto_queue: boolean;
  // Auto-publish tended content (persisted; pipeline consumes later)
  auto_publish: boolean;
  /** Brand color palette: up to 6 '#rrggbb' strings, lowercase, ordered. */
  color_palette: string[];
  /** Playbooks: role → hex (background, paper, ink, accent, accent_2, …); sparse — the brand's pinned roles */
  color_roles?: Record<string, string>;
  /** The layer beneath the roles: the universal map every brand inherits until it pins its own (QA bucket 1). */
  universal_color_roles?: Record<string, string>;
  /** Brand over universal over TEND's first pass — what a playbook reads. */
  effective_color_roles?: Record<string, string>;
  /** role → brand | universal | auto */
  color_role_sources?: Record<string, 'brand' | 'universal' | 'auto'>;
  /** The layer beneath: universal palette — a non-empty brand palette wins. */
  universal_color_palette: string[];
  /** Page background override: '#rrggbb' replaces the dreamy sky (video not
      mounted); null = the universal sky atmosphere. */
  background_color: string | null;
  /** Per-channel aspect overrides: sparse {platform: bucket} map; a missing
      platform inherits universal. PATCH semantics: whole-map replace. */
  channel_aspect: Record<string, string>;
  /** The layer beneath: full per-platform universal aspect map. */
  universal_channel_aspect: Record<string, string>;
  /** Universal safe zones (channel → placement → insets in % + bucket);
      seeds merged with the stored universal value. Universal only. */
  universal_safe_zones?: Record<string, Record<string, { top: number; bottom: number; left: number; right: number; bucket: string }>>;
  /** Editorial cadence brand overrides: {channels: {platform: sparse field
      map}, order: array|null}. Missing field/platform inherits universal.
      PATCH semantics: whole-value replace of the override structure. */
  editorial_cadence: { channels: Record<string, any>; order: string[] | null } | Record<string, never>;
  /** The layer beneath: full universal cadence {channels (all 13, all
      fields), order (permutation of the 13)}. */
  universal_editorial_cadence: { channels: Record<string, any>; order: string[] };
  /** Sparse link-settings override (shortener toggle + UTM param map);
      missing fields inherit universal. PATCH semantics: whole-value
      replace; null clears the override. */
  link_settings: Record<string, any>;
  /** The layer beneath: universal link settings, already default-merged. */
  universal_link_settings: Record<string, any>;
  /** Validated subreddit list (managed via the reddit-destinations
      endpoints, NOT PATCH): [{name, kind, flairs, added_at}]. */
  reddit_destinations?: RedditDestination[];
  /** Connected Reddit username — labels the implicit profile default. */
  reddit_username?: string | null;
  /** Public social profile URLs: sparse {channel_id: url} map keyed by the
      13 TEND channel ids ('youtube_shorts' for YouTube). A live Ayrshare
      connection's profile_url supersedes these at read time. PATCH
      semantics: whole-map replace. */
  social_links: Record<string, string>;
  /** S3 copy of the brand's default template-preview image (Design modal
      easter egg). Read-only here — set/cleared via the dedicated
      template-preview-image endpoints so the S3 copy always happens.
      Null = previews use background_color. */
  template_preview_image_url?: string | null;
  /** The layer beneath: universal default template-preview image (S3 copy,
      superadmin-picked from any brand's pool). Priority: brand image →
      universal image → background_color fill. */
  universal_template_preview_image_url?: string | null;
}

// Content-type template defaults
export interface OverlayTemplateDefaultSlot {
  id: number | null;
  template_id: number | null;
  scope: 'global' | 'brand' | null;
  brand_id: number | null;
  template: OverlayTemplateResponse | null;
}

export interface OverlayTemplateDefaults {
  post: OverlayTemplateDefaultSlot;
  carousel_intro: OverlayTemplateDefaultSlot;
  carousel_body: OverlayTemplateDefaultSlot;
  carousel_outro: OverlayTemplateDefaultSlot;
  video: OverlayTemplateDefaultSlot;
}

// Per-tactic default template (Editorial ▾ Tactics / tactic modal slot).
// Which row is in effect, in order: the brand's own tactic row → the
// universal tactic row → the post-type default for the tactic's post type.
export type TacticTemplateDefaultSource = 'brand_tactic' | 'global_tactic' | 'post_default' | null;

export interface TacticTemplateDefaultResolution {
  tactic_ref: string;                                  // "{platform}:{tactic_id}"
  post_type: string;
  take?: boolean;                                      // frontmatter switch; single-post takes fall back to 'video'
  fallback_key: 'post' | 'video' | 'carousel_body';    // the post-type slot it inherits from
  fallback_label: string;                              // "Post default" | "Video default" | "Carousel default"
  brand: OverlayTemplateDefaultSlot | null;            // the brand's own tactic row (Brand scope only)
  global: OverlayTemplateDefaultSlot | null;           // the universal tactic row
  post_default: OverlayTemplateDefaultSlot | null;     // resolved post-type default for the fallback_key
  effective: { source: TacticTemplateDefaultSource; slot: OverlayTemplateDefaultSlot | null };
}

// Pre-prompt types for SuperAdmin management
export interface PrePrompt {
  id: number;
  name: string;
  prompt: string;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string | null;
  scope: 'global' | 'brand' | 'platform' | 'brand+platform';
  brand_id: number | null;
  platform: string | null;
  position: 'system' | 'post' | 'carousel' | 'insights' | 'agent';  // 'system' = top, 'post' = end, 'carousel' = carousel posts only, 'insights' = analytics advisor
  /** OpenRouter model id the prompt runs against (insights position; null = platform default) */
  model: string | null;
}

export interface PrePromptCreateRequest {
  name: string;
  prompt: string;
  is_active?: boolean;
  created_by: string;
  scope?: 'global' | 'brand' | 'platform' | 'brand+platform';
  brand_id?: number | null;
  platform?: string | null;
  position?: 'system' | 'post' | 'carousel' | 'insights' | 'agent';
  model?: string | null;
}

export interface PrePromptUpdateRequest {
  name?: string;
  prompt?: string;
  is_active?: boolean;
  scope?: 'global' | 'brand' | 'platform' | 'brand+platform';
  brand_id?: number | null;
  platform?: string | null;
  position?: 'system' | 'post' | 'carousel' | 'insights' | 'agent';
  /** Empty string clears back to platform default */
  model?: string | null;
}

// Pre-prompt API functions
export const prePromptApi = {
  // List pre-prompts with optional filters
  list: async (params?: {
    brand_id?: number;
    platform?: string;
    scope?: string;
    is_active?: boolean;
  }): Promise<PrePrompt[]> => {
    const searchParams = new URLSearchParams();
    if (params?.brand_id !== undefined) searchParams.append('brand_id', String(params.brand_id));
    if (params?.platform) searchParams.append('platform', params.platform);
    if (params?.scope) searchParams.append('scope', params.scope);
    if (params?.is_active !== undefined) searchParams.append('is_active', String(params.is_active));

    const queryString = searchParams.toString();
    const url = `${API_BASE_URL}/tendv2/pre-prompts${queryString ? `?${queryString}` : ''}`;
    const response = await authedFetch(url);
    if (!response.ok) throw new Error('Failed to fetch pre-prompts');
    return response.json();
  },

  // Get single pre-prompt by ID
  get: async (id: number): Promise<PrePrompt> => {
    const response = await authedFetch(`${API_BASE_URL}/tendv2/pre-prompts/${id}`);
    if (!response.ok) throw new Error('Pre-prompt not found');
    return response.json();
  },

  // Create new pre-prompt
  create: async (data: PrePromptCreateRequest): Promise<PrePrompt> => {
    const response = await authedFetch(`${API_BASE_URL}/tendv2/pre-prompts`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create pre-prompt');
    return response.json();
  },

  // Update existing pre-prompt
  update: async (id: number, data: PrePromptUpdateRequest): Promise<PrePrompt> => {
    const response = await authedFetch(`${API_BASE_URL}/tendv2/pre-prompts/${id}`, {
      method: 'PUT',
      headers: authJsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update pre-prompt');
    return response.json();
  },

  // Delete pre-prompt
  delete: async (id: number): Promise<void> => {
    const response = await authedFetch(`${API_BASE_URL}/tendv2/pre-prompts/${id}`, {
      method: 'DELETE',
      headers: authOnlyHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete pre-prompt');
  },
};

// ====================================================================
// VIDEO RENDER TYPES AND API
// ====================================================================

export interface VideoRenderRequest {
  // Source video
  video_url: string;
  video_duration?: number; // seconds

  // Platform for output dimensions
  platform: string;

  // Overlay configuration (same as image overlays)
  overlay_config: OverlayTemplateConfig;

  // Logo
  logo_url?: string;
  logo_key?: string;
  logo_display_mode?: string;
  brand_name: string;
  // Brand slug — drives the font manifest (brand fonts + brand/universal
  // font defaults) for the video render. Backend falls back to the
  // transformation's brand when omitted.
  brand_slug?: string;

  // Text content
  title?: string;
  subheader?: string;
  body?: string;
  category?: string;

  // Category styling
  category_border_radius?: number;
  category_bg_color?: string;

  // Image fit/fill mode
  image_fit_mode?: string;
  image_fit_anchor?: string;
  image_fit_width?: number;
  image_fill_position_x?: number;
  image_fill_position_y?: number;

  // Animation settings (Phase 1.5)
  animation_settings?: {
    animations_enabled?: boolean;
    logo_animation?: 'none' | 'fade' | 'slide-up' | 'slide-left' | 'scale';
    title_animation?: 'none' | 'fade' | 'slide-up' | 'typewriter';
    subheader_animation?: 'none' | 'fade' | 'slide-up';
    category_animation?: 'none' | 'fade' | 'pulse' | 'scale';
    gradient_animation?: 'none' | 'fade';
    animation_duration?: number;
    animation_easing?: 'linear' | 'ease-out' | 'spring';
    stagger_delay?: number;
  };
}

export interface VideoRenderJob {
  id: number;
  content_id: number;
  status: 'queued' | 'rendering' | 'completed' | 'failed';
  input_props_json?: string;
  output_url?: string;
  width?: number;
  height?: number;
  duration_seconds?: number;
  created_at: string;
  completed_at?: string;
  error_message?: string;
}

export interface VideoRenderResponse {
  success: boolean;
  job_id?: number;
  status?: string;
  output_url?: string;
  error?: string;
}

// Video render API functions
export const videoRenderApi = {
  /**
   * Queue a video render job with overlay
   * @param contentId - TendV2 content item ID
   * @param request - Video render request with overlay config
   * @returns Job info with ID for polling
   */
  queueRender: async (
    contentId: number,
    request: VideoRenderRequest
  ): Promise<VideoRenderResponse> => {
    try {
      const renderAuthToken = getStoredToken();
      const response = await authedFetch(`${API_BASE_URL}/tend/tendv2/content/${contentId}/render-video`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(renderAuthToken ? { Authorization: formatAuthHeader(renderAuthToken) } : {}),
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.detail || `Failed to queue video render: ${response.statusText}`,
        };
      }

      const data = await response.json();
      return {
        success: true,
        job_id: data.job_id,
        status: data.status,
      };
    } catch (error) {
      console.error('Error queueing video render:', error);
      return { success: false, error: String(error) };
    }
  },

  /**
   * Get the status of a video render job
   * @param jobId - Video render job ID
   * @returns Job status and output URL if completed
   */
  getJobStatus: async (jobId: number): Promise<VideoRenderJob | null> => {
    try {
      const response = await authedFetch(`${API_BASE_URL}/tend/tendv2/video-render-jobs/${jobId}`, {
        method: 'GET',
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        console.warn(`Failed to get video render job ${jobId}`);
        return null;
      }

      const data = await response.json();
      // API returns {success: true, job: {...}} - extract the job object
      return data.job || null;
    } catch (error) {
      console.error('Error getting video render job:', error);
      return null;
    }
  },

  /**
   * Poll a video render job until completion
   * @param jobId - Video render job ID
   * @param maxAttempts - Maximum polling attempts (default 60)
   * @param intervalMs - Polling interval in ms (default 2000)
   * @returns Completed job with output URL, or failed job with error
   */
  pollJobCompletion: async (
    jobId: number,
    maxAttempts: number = 60,
    intervalMs: number = 2000
  ): Promise<VideoRenderJob | null> => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const job = await videoRenderApi.getJobStatus(jobId);

      if (!job) {
        // Job not found, wait and retry
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
        continue;
      }

      if (job.status === 'completed' || job.status === 'failed') {
        return job;
      }

      // Still rendering, wait and retry
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    console.warn(`Video render job ${jobId} polling timed out`);
    return null;
  },

  /**
   * List video render jobs for a content item
   * @param contentId - TendV2 content item ID
   * @returns Array of video render jobs
   */
  listJobs: async (contentId: number): Promise<VideoRenderJob[]> => {
    try {
      const response = await authedFetch(
        `${API_BASE_URL}/tend/tendv2/content/${contentId}/video-render-jobs`,
        {
          method: 'GET',
          headers: authJsonHeaders(),
        }
      );

      if (!response.ok) {
        console.warn(`Failed to list video render jobs for content ${contentId}`);
        return [];
      }

      const data = await response.json();
      // API returns {success: true, jobs: [...]} - extract the jobs array
      return data.jobs || [];
    } catch (error) {
      console.error('Error listing video render jobs:', error);
      return [];
    }
  },
};

// ============================================================================
// CAROUSEL / VIDEO SERIES SLIDE TYPES & API
// ============================================================================

/**
 * Content mode for carousel/video series support
 */
export type ContentMode = 'single' | 'carousel' | 'video_series';

/**
 * A single slide within a carousel or video series
 */
export interface CarouselSlide {
  id: number;
  content_id: number;
  slide_number: number;
  // Media - selected/active media for this slide
  media_url?: string;
  media_type?: 'image' | 'video';
  // Media pool (inherited from parent content) - all available images
  media_urls?: string[];
  media_order_custom?: boolean;
  primary_image_url?: string;
  // Overlay
  overlay_config?: OverlayTemplateConfig | null;
  overlay_template_id?: number;
  // Image fit/fill
  image_fit_mode?: string | null;
  image_fit_anchor?: string | null;
  image_fit_width?: number | null;
  image_fill_position_x?: number | null;
  image_fill_position_y?: number | null;
  // Text content
  generated_title?: string;
  generated_subheader?: string;
  generated_body?: string;  // AI-generated body paragraph (replaces slide_text)
  script?: string;          // Spoken script for the recording teleprompter (~20-40s)
  custom_title?: string;
  custom_subheader?: string;
  title?: string;      // Effective title (custom overrides generated)
  subheader?: string;  // Effective subheader
  slide_text?: string; // DEPRECATED: prefer generated_body
  // Video fields
  video_start_time?: number;
  video_end_time?: number;
  duration_seconds?: number;
  // Rendered output
  rendered_overlay_url?: string;
  rendered_video_url?: string;
  // Timestamps
  created_at?: string;
  updated_at?: string;
}

/**
 * Request to create a new slide
 */
export interface SlideCreateRequest {
  slide_number?: number;  // Auto-assigned if not provided
  media_url?: string;
  media_type?: 'image' | 'video';
  // Media pool (inherited from parent content)
  media_urls?: string[];
  primary_image_url?: string;
  overlay_config?: OverlayTemplateConfig;
  overlay_template_id?: number;
  generated_title?: string;
  generated_subheader?: string;
  generated_body?: string;
  custom_title?: string;
  custom_subheader?: string;
  slide_text?: string; // DEPRECATED: prefer generated_body
  video_start_time?: number;
  video_end_time?: number;
  duration_seconds?: number;
}

/**
 * Request to update a slide
 */
export interface SlideUpdateRequest {
  media_url?: string;
  media_type?: 'image' | 'video';
  // Media pool (inherited from parent content)
  media_urls?: string[];
  media_order_custom?: boolean;
  primary_image_url?: string;
  overlay_config?: OverlayTemplateConfig;
  overlay_template_id?: number | null;  // null clears the template reference
  custom_title?: string;
  custom_subheader?: string;
  generated_body?: string;
  script?: string;
  slide_text?: string; // DEPRECATED: prefer generated_body
  video_start_time?: number;
  video_end_time?: number;
  duration_seconds?: number;
  rendered_overlay_url?: string;
  rendered_video_url?: string;
  // Image fit/fill mode
  image_fit_mode?: string;
  image_fit_anchor?: string;
  image_fit_width?: number;
  image_fill_position_x?: number;
  image_fill_position_y?: number;
}

/**
 * Platform-specific carousel limits.
 * Offline FALLBACK only — the backend is the source of truth; always prefer
 * getPlatformCarouselLimits(), which fetches the canonical dict from
 * api_gateway.models.tendv2.PLATFORM_CAROUSEL_LIMITS.
 */
export const PLATFORM_CAROUSEL_LIMITS: Record<string, number> = {
  instagram: 10,
  facebook: 10,
  linkedin: 9,
  twitter: 4,
  threads: 20,
  pinterest: 5,
  tiktok: 35,
  bluesky: 4,
  youtube_shorts: 1,
};

let _carouselLimitsPromise: Promise<Record<string, number>> | null = null;

/**
 * Post-text character ceilings + the length a short link takes in the post.
 * Served by GET /tendv2/platform-limits from the same table the queue
 * dispatcher holds on, so the card counter and the hold rule agree.
 */
export interface PlatformCharLimits {
  limits: Record<string, number>;
  default_limit: number;
  /** base + "/" + slug; null when no shortener is configured (the post
      carries the full parameterized article URL then). */
  short_link_length: number | null;
}

/** window event: an edit moved a queued item's ticket — lifted or applied a system hold (detail.contentId). */
export const QUEUE_RESUMED_EVENT = 'tend:queue-resumed';
/** A pill's Publish now landed — every other mounted pill for the item
    (list row behind the modal, strip card) re-reads its publish status so
    none of them keeps offering Queue / Publish for a post that is live. */
export const PUBLISHED_EVENT = 'tend:published';

let _charLimitsPromise: Promise<PlatformCharLimits> | null = null;

export const getPlatformCharLimits = (fallback: PlatformCharLimits): Promise<PlatformCharLimits> => {
  if (!_charLimitsPromise) {
    _charLimitsPromise = authedFetch(`${API_BASE_URL}/tend/tendv2/platform-limits`)
      .then((r) => {
        if (!r.ok) throw new Error('Failed to fetch platform limits');
        return r.json();
      })
      .then((d) => ({
        limits: (d.limits || fallback.limits) as Record<string, number>,
        default_limit: d.default_limit ?? fallback.default_limit,
        short_link_length: d.short_link_length ?? null,
      }))
      .catch((err) => {
        console.warn('Falling back to static platform limits:', err);
        _charLimitsPromise = null;
        return fallback;
      });
  }
  return _charLimitsPromise;
};


/**
 * Fetch the canonical per-platform carousel slide ceilings from the backend,
 * cached for the session. Falls back to the static dict if unreachable.
 */
export const getPlatformCarouselLimits = (): Promise<Record<string, number>> => {
  if (!_carouselLimitsPromise) {
    _carouselLimitsPromise = authedFetch(`${API_BASE_URL}/tend/tendv2/carousel-limits`)
      .then((r) => {
        if (!r.ok) throw new Error('Failed to fetch carousel limits');
        return r.json();
      })
      .then((d) => d.limits as Record<string, number>)
      .catch((err) => {
        console.warn('Falling back to static carousel limits:', err);
        _carouselLimitsPromise = null;
        return PLATFORM_CAROUSEL_LIMITS;
      });
  }
  return _carouselLimitsPromise;
};

/**
 * Carousel/Slide API methods
 */
export const carouselApi = {
  /**
   * List all slides for a content item
   */
  listSlides: async (contentId: number): Promise<CarouselSlide[]> => {
    try {
      const response = await authedFetch(
        `${API_BASE_URL}/tend/tendv2/content/${contentId}/slides`,
        {
          method: 'GET',
          headers: authJsonHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch slides');
      }

      return response.json();
    } catch (error) {
      console.error('Error fetching slides:', error);
      return [];
    }
  },

  /**
   * Create a new slide
   */
  createSlide: async (
    contentId: number,
    data: SlideCreateRequest
  ): Promise<CarouselSlide> => {
    const response = await authedFetch(
      `${API_BASE_URL}/tend/tendv2/content/${contentId}/slides`,
      {
        method: 'POST',
        headers: authJsonHeaders(),
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to create slide');
    }

    return response.json();
  },

  /**
   * Get a single slide by ID
   */
  getSlide: async (slideId: number): Promise<CarouselSlide> => {
    const response = await authedFetch(
      `${API_BASE_URL}/tend/tendv2/slides/${slideId}`,
      {
        method: 'GET',
        headers: authJsonHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch slide');
    }

    return response.json();
  },

  /**
   * Update a slide
   */
  updateSlide: async (
    slideId: number,
    data: SlideUpdateRequest
  ): Promise<CarouselSlide> => {
    const response = await authedFetch(
      `${API_BASE_URL}/tend/tendv2/slides/${slideId}`,
      {
        method: 'PATCH',
        headers: authJsonHeaders(),
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to update slide');
    }

    return response.json();
  },

  /**
   * Delete a slide
   */
  deleteSlide: async (slideId: number): Promise<void> => {
    const response = await authedFetch(
      `${API_BASE_URL}/tend/tendv2/slides/${slideId}`,
      {
        method: 'DELETE',
        headers: authJsonHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to delete slide');
    }
  },

  /**
   * Reorder slides
   */
  reorderSlides: async (
    contentId: number,
    slideIds: number[]
  ): Promise<void> => {
    const response = await authedFetch(
      `${API_BASE_URL}/tend/tendv2/content/${contentId}/slides/reorder`,
      {
        method: 'POST',
        headers: authJsonHeaders(),
        body: JSON.stringify({ slide_ids: slideIds }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to reorder slides');
    }
  },

  /**
   * Render a slide's overlay
   */
  renderSlide: async (
    slideId: number,
    options?: { platform?: string; force_rerender?: boolean }
  ): Promise<{
    slide_id: number;
    rendered_overlay_url?: string;
    rendered_video_url?: string;
    status: string;
    message?: string;
  }> => {
    const response = await authedFetch(
      `${API_BASE_URL}/tend/tendv2/slides/${slideId}/render`,
      {
        method: 'POST',
        headers: authJsonHeaders(),
        body: JSON.stringify(options || {}),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to render slide');
    }

    return response.json();
  },

  /**
   * Update content mode (single, carousel, video_series)
   */
  updateContentMode: async (
    contentId: number,
    contentMode: ContentMode,
    initialSlideCount?: number
  ): Promise<{
    message: string;
    content_mode: ContentMode;
    slide_count: number;
  }> => {
    const response = await authedFetch(
      `${API_BASE_URL}/tend/tendv2/content/${contentId}/mode`,
      {
        method: 'PATCH',
        headers: authJsonHeaders(),
        body: JSON.stringify({
          content_mode: contentMode,
          initial_slide_count: initialSlideCount,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to update content mode');
    }

    return response.json();
  },

  /**
   * Get carousel limits for a content item
   */
  getCarouselLimits: async (
    contentId: number
  ): Promise<{
    content_id: number;
    platform: string;
    slide_count: number;
    platform_limit: number;
    within_limit: boolean;
    all_limits: Record<string, number>;
  }> => {
    const response = await authedFetch(
      `${API_BASE_URL}/tend/tendv2/content/${contentId}/carousel-limits`,
      {
        method: 'GET',
        headers: authJsonHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get carousel limits');
    }

    return response.json();
  },

  /**
   * Validate carousel content against platform limits before publishing
   */
  validateCarouselForPublish: async (
    contentId: number,
    platforms: string[]
  ): Promise<{
    status: string;
    is_carousel: boolean;
    slide_count?: number;
    platform_limits?: Record<
      string,
      { limit: number; slides: number; will_truncate: boolean }
    >;
    warnings?: string[];
    valid?: boolean;
  }> => {
    const params = new URLSearchParams();
    platforms.forEach((p) => params.append('platforms', p));

    const response = await authedFetch(
      `${API_BASE_URL}/tend/tendv2/content/${contentId}/validate-carousel-publish?${params}`,
      {
        method: 'POST',
        headers: authJsonHeaders(),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to validate carousel');
    }

    return response.json();
  },
};
